// src/chat-platform/ChatApp.jsx
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { Plus, AlignLeft, Sparkles, Lightbulb } from "lucide-react";
import ModelSelector from "../components/ModelSelector";
import DevToolsMenu, {
  isDevToolsEnabled,
  isDevModelEnabled,
  isDevAgentEnabled,
  isDevSkillEnabled,
} from "./components/DevToolsMenu";
import { useAgentSelection } from "./hooks/useAgentSelection";
import { useDevToolsActions } from "./store/hooks";
import ChatContextChipRow from "./chat-context/ChatContextChipRow";
import MentionPopover from "./chat-context/MentionPopover";
import {
  useChatContextActions,
  useChatContexts,
  useCurrentPageContext,
} from "./chat-context/hooks";
import { selectCurrentPageMeta } from "./chat-context/chatContextSlice";
import VoiceButton from "../components/VoiceButton";
import VoiceUploadButton from "../components/VoiceUploadButton";
import VoiceInputButton from "../components/VoiceInputButton";
import VoiceInputRecording from "../components/VoiceInputRecording";
import {
  apiClient,
  getUserId,
  isAuthenticated,
  userApi,
  authApi,
  API_BASE_URL,
  getAccessToken,
  shareApi,
  adminApi,
} from "../utils/api";
import { clearStorageForLogout } from "../utils/storageUtils";
import { Storage } from "../constants/storage";
import NotificationDraftView from "../components/NotificationDraftView";
import { formatFileSize, buildFallbackToolName } from "../utils/helpers";
import { extractUniqueURLs } from "../utils/urlDetector";
import { computeFileSHA256 } from "../utils/fileHashUtils";
import { InputWithInlineURLs } from "../components/InlineURLText";
import {
  parseSSEEvents,
  mergeToolBlocks,
  extractQuestions,
} from "../components/MessageBlockRenderer";
import { QuestionUI } from "../components/QuestionUI";
import { ChatStreamView, useChatStreamBlocks } from "../components/chatstream";
import { TodoProgressCompact } from "../components/TodoProgressPanel";
import { config } from "../config";
import { useUsageLimit } from "../hooks/useUsageLimit";
import { showError } from "../utils/toastUtils";
import { useTheme } from "../hooks/useTheme";
import { useVoiceChat } from "../hooks/useVoiceChat";
import { useVoiceInput } from "../hooks/useVoiceInput";
import { useAgentWebSocket } from "../hooks/useAgentWebSocket";
import { getAgentWS, destroyAgentWS } from "../utils/ws";
import { useChatPlatform } from "./context/ChatPlatformContext";
import {
  useSessions,
  useSessionsActions,
  useSessionLoader,
  useSessionLoadRequest,
  useSessionsStatus,
  useChatQueue,
  useChatQueueActions,
  useChatSelector,
} from "./store/hooks";
import { useInputSectionMetrics } from "./hooks/useInputSectionMetrics";
import { useSuggestions } from "./hooks/useSuggestions";
import { useNotificationCount } from "./hooks/useNotificationCount";
import { useSidebarPersistence } from "./hooks/useSidebarPersistence";
import { useRightPanelSync } from "./hooks/useRightPanelSync";
import RightPanel from "./sections/RightPanel";
import ChatModals from "./sections/ChatModals";
import ScrollDownButton from "./sections/ScrollDownButton";
import UploadedFileItem from "./sections/UploadedFileItem";
import ShareLoader from "./sections/ShareLoader";
import BottomFadeMask from "./sections/BottomFadeMask";
import ComposerSendButton from "./sections/ComposerSendButton";
import {
  MAX_FILES,
  MAX_FILE_SIZE_MB,
  FILE_SIZE_LIMITS,
  ALLOWED_FILE_TYPES,
} from "./constants/upload";
import {
  MESSAGES_BASE_SPACING,
  MASK_FADE_HEIGHT,
  MASK_FADE_HEIGHT_SHARE_UNAUTH,
  MASK_FADE_HEIGHT_MOBILE,
  MASK_BUFFER,
} from "./constants/layout";
import { getRandomGreeting } from "./utils/greeting";
import { syncChatUrl } from "./utils/chatUrlSync";
import NewHomePage from "@/components/nebula/NewHomePage";

function getIconTypeFromMime(mimeType, filename) {
  if (mimeType?.startsWith("image/")) return "image";
  const ext = filename?.split(".").pop()?.toLowerCase();
  const map = {
    pdf: "pdf",
    doc: "doc",
    docx: "docx",
    xls: "xlsx",
    xlsx: "xlsx",
    ppt: "pptx",
    pptx: "pptx",
    txt: "txt",
    csv: "csv",
    json: "json",
    md: "md",
  };
  return map[ext] || "file";
}

function isTodoToolName(name) {
  const n = (name || "").toLowerCase();
  return n.includes("todo") || n === "write_todos";
}

function handleTodoSSEEvent(data, setTodosProgress) {
  if (
    data.type === "content_block_start" &&
    data.content_block?.type === "tool_use"
  ) {
    if (
      isTodoToolName(data.content_block.name) &&
      !data.content_block.parent_tool_use_id
    ) {
      const todos = data.content_block.input?.todos;
      if (todos && Array.isArray(todos)) {
        setTodosProgress(todos);
      }
    }
  }
}

function handleSearchSourceSSEEvent(data, setSearchSources) {
  if (
    data.type === "content_block_start" &&
    data.content_block?.type === "tool_result"
  ) {
    const artifact = data.content_block.artifact;
    const label = artifact?.label || artifact?.query;
    if (label && artifact?.sources?.length > 0) {
      setSearchSources((prev) => {
        const current = prev || [];
        const existingIdx = current.findIndex((g) => g.label === label);
        if (existingIdx >= 0) {
          const existing = current[existingIdx];
          const seenUrls = new Set(existing.sources.map((s) => s.url));
          const newSources = artifact.sources.filter(
            (s) => !seenUrls.has(s.url),
          );
          if (newSources.length === 0) return current;
          const updated = [...current];
          updated[existingIdx] = {
            ...existing,
            sources: [...existing.sources, ...newSources],
          };
          return updated;
        }
        return [...current, { ...artifact, label }];
      });
    }
  }
}

function handleWorkspaceFilesSSEEvent(data, setWorkspaceFiles) {
  if (
    data.type === "content_block_start" &&
    data.content_block?.type === "tool_result"
  ) {
    const name = data.content_block.name;
    if (
      (name === "write_file" || name === "edit_file") &&
      data.content_block.artifact
    ) {
      const fileInfo = data.content_block.artifact;
      if (fileInfo.path && fileInfo.filename) {
        setWorkspaceFiles((prev) => {
          const current = prev || [];
          const filtered = current.filter((f) => f.path !== fileInfo.path);
          return [...filtered, fileInfo];
        });
      }
    }
    if (name === "send_file_to_user" && data.content_block.artifact) {
      const files = Array.isArray(data.content_block.artifact)
        ? data.content_block.artifact
        : [data.content_block.artifact];
      setWorkspaceFiles((prev) => {
        const current = prev || [];
        const byPath = Object.fromEntries(current.map((f) => [f.path, f]));
        for (const f of files) {
          if (f.path && f.filename) byPath[f.path] = f;
        }
        return Object.values(byPath);
      });
    }
  }
}

const HUB_ALIAS = "nebula-assistant";

const EMBEDDED_DRAFT_SUGGESTIONS = [
  {
    label: "Tóm tắt nội dung trang này",
    prompt: "Tóm tắt nội dung trang hiện tại giúp tôi",
    icon: AlignLeft,
  },
  {
    label: "Phân tích insight chính",
    prompt: "Phân tích các insight quan trọng của trang hiện tại",
    icon: Sparkles,
  },
];

const ChatApp = () => {
  const userId = getUserId();
  const { sessionId: urlSessionId, shareId, adminViewSessionId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { agentId, agentName, setAgent, clearAgentId, hydrateAgentName } =
    useAgentSelection();
  const {
    fetchAgents: fetchDevAgentsAction,
    fetchSkills: fetchDevSkillsAction,
    fetchModels: fetchDevModelsAction,
  } = useDevToolsActions();
  useEffect(() => {
    if (!isDevToolsEnabled()) return;
    if (isDevModelEnabled()) fetchDevModelsAction();
    if (isDevAgentEnabled()) fetchDevAgentsAction();
    if (isDevSkillEnabled()) fetchDevSkillsAction();
  }, [fetchDevAgentsAction, fetchDevSkillsAction, fetchDevModelsAction]);
  const sourceParam = searchParams.get("source");
  const sourceIdParam = searchParams.get("source_id");
  const queryParam = searchParams.get("q");
  const navigate = useNavigate();
  const buildPath = useCallback(
    (path) => {
      if (!agentId) return path;
      const sep = path.includes("?") ? "&" : "?";
      return `${path}${sep}agent_id=${agentId}`;
    },
    [agentId],
  );

  // Initialize theme from URL parameter (?theme=blue or ?theme=orange)
  useTheme();
  const {
    activeSessionId,
    setActiveSessionId,
    isMobile,
    setIsMobile,
    isSidebarOpen,
    setIsSidebarOpen,
    wasOpenBeforeResize,
    setWasOpenBeforeResize,
    isStreaming,
    setIsStreaming,
    currentUser,
    setCurrentUser,
    setShowNotificationPanel,
    setNotificationCount,
    setEditingSessionId,
    editingName,
    setEditingName,
    setIsLoginOpen,
    isUserMenuOpen,
    setIsUserMenuOpen,
    setMembershipLabel,
    isLoading,
    setIsLoading,
    isInitializing,
    setIsInitializing,
    setIsInputDisabled,
    isShareView,
    setIsShareView,
    isAdminView,
    setIsAdminView,
    isDraftChat,
    setIsDraftChat,
    sharedInfo,
    setSharedInfo,
    isEmbedded,
    isRightPanelOpen,
    setIsRightPanelOpen,
    setHasRightPanelContent,
    registerChatCallbacks,
  } = useChatPlatform();
  const sessions = useSessions();
  const { replaceSessions, addSession, updateSession, removeSession } =
    useSessionsActions();
  const { consumeLoad } = useSessionLoader();
  const loadRequest = useSessionLoadRequest();
  const chatQueue = useChatQueue();
  const { consume: consumeChatQueue, send: sendToChatQueue } =
    useChatQueueActions();
  const sessionsStatus = useSessionsStatus();
  const sessionId = activeSessionId || urlSessionId;
  const [, setHubPendingCount] = useState(0);
  const [contextDraft, setContextDraft] = useState(null);
  const contextDraftRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isSwitchingSession, setIsSwitchingSession] = useState(false);
  const [isWsConnected, setIsWsConnected] = useState(
    () => getAgentWS().isConnected,
  );
  const isInitializingRef = useRef(true);
  useEffect(() => {
    isInitializingRef.current = isInitializing;
  }, [isInitializing]);
  const activeSessionIdRef = useRef(activeSessionId);
  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);
  const [isMessagesVisible, setIsMessagesVisible] = useState(
    () => !activeSessionId,
  );
  const [greetingMessage, setGreetingMessage] = useState("");
  const [hasInputText, setHasInputText] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const contextFeatureEnabled = !isShareView && !isAdminView;
  const messagesEndRef = useRef(null);
  const chatContentRef = useRef(null);
  const inputRef = useRef(null);
  const chatContexts = useChatContexts();
  const contextsSendRef = useRef([]);
  useEffect(() => {
    contextsSendRef.current = chatContexts.map((c) => ({
      type: c.type,
      uri: c.uri,
    }));
  }, [chatContexts]);
  const currentPageContext = useCurrentPageContext();
  const pageMeta = useChatSelector(selectCurrentPageMeta);
  const suggestionsStatus = pageMeta?.suggestionsStatus || "idle";
  const pageExpectsSuggestions = currentPageContext?.type === "insight";
  const pageSuggestionsReady = pageExpectsSuggestions
    ? suggestionsStatus === "ready"
    : suggestionsStatus !== "loading";
  const embeddedDraftSuggestions = useMemo(() => {
    const pageList = Array.isArray(pageMeta?.suggestions)
      ? pageMeta.suggestions
      : null;
    if (pageList && pageList.length) {
      const summaryDefault = EMBEDDED_DRAFT_SUGGESTIONS.find(
        (s) => s.label === "Tóm tắt nội dung trang này",
      );
      const ctaEnriched = pageList.map((s) => ({ icon: Lightbulb, ...s }));
      const seen = new Set(ctaEnriched.map((s) => s.label));
      const head =
        summaryDefault && !seen.has(summaryDefault.label)
          ? [summaryDefault]
          : [];
      return [...head, ...ctaEnriched];
    }
    return EMBEDDED_DRAFT_SUGGESTIONS;
  }, [pageMeta?.suggestions]);
  const { add: addChatContext } = useChatContextActions();
  const acceptCurrentPageMention = useCallback(() => {
    if (!currentPageContext) {
      setMentionOpen(false);
      return;
    }
    addChatContext({ ...currentPageContext, source: "mention" });
    if (inputRef.current) {
      inputRef.current.value = (inputRef.current.value || "").replace(
        /@\s*$/,
        "",
      );
      setHasInputText(!!inputRef.current.value);
    }
    setMentionOpen(false);
  }, [currentPageContext, addChatContext]);
  const detectMentionTrigger = useCallback(() => {
    if (!inputRef.current) {
      setMentionOpen(false);
      return;
    }
    const v = inputRef.current.value || "";
    const caret =
      typeof inputRef.current.element?.selectionStart === "number"
        ? inputRef.current.element.selectionStart
        : v.length;
    const ch = v[caret - 1];
    const prev = caret >= 2 ? v[caret - 2] : " ";
    const triggered = ch === "@" && (caret === 1 || /\s/.test(prev));
    setMentionOpen((prevOpen) =>
      triggered ? true : prevOpen ? false : prevOpen,
    );
  }, []);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const [liveMessages, setLiveMessages] = useState([]);
  const liveMessagesStartRef = useRef(null);

  const [todosProgress, setTodosProgress] = useState(null);
  const [searchSources, setSearchSources] = useState(null);
  const [workspaceFiles, setWorkspaceFiles] = useState(null);
  const [viewingAttachment, setViewingAttachment] = useState(null);
  useRightPanelSync({
    todosProgress,
    searchSources,
    workspaceFiles,
    setHasRightPanelContent,
    isRightPanelOpen,
    isEmbedded,
  });

  // Debug mode: collect raw server events for display
  const DEBUG_ENABLED = import.meta.env.VITE_DEBUG_MODE === "true";
  const MODEL_SELECTOR_ENABLED =
    import.meta.env.VITE_ENABLE_MODEL_SELECTOR !== "false";
  const [debugRawEvents, setDebugRawEvents] = useState([]);
  const debugRawEventsRef = useRef([]);
  const [debugRawHistory, setDebugRawHistory] = useState(null);

  const chatStream = useChatStreamBlocks();

  // V2 API: Interrupt/HITL state for approval handling
  const [interruptSessionId, setInterruptSessionId] = useState(null);
  const [interruptData, setInterruptData] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [debugViewMode, setDebugViewMode] = useState("markdown");

  const resetInterruptState = useCallback(() => {
    setInterruptSessionId(null);
    setInterruptData(null);

    setIsStopping(false);
    isStoppingRef.current = false;
    if (stoppingTimeoutRef.current) {
      clearTimeout(stoppingTimeoutRef.current);
      stoppingTimeoutRef.current = null;
    }
  }, []);

  const restoreInterruptFromHistory = useCallback(
    (historyData, sessionId) => {
      const isAgentAlive = historyData.agent_status === "running";
      if (
        historyData.status === "waiting_approval" &&
        historyData.pending_approval &&
        isAgentAlive
      ) {
        const pending = historyData.pending_approval;
        setInterruptSessionId(pending.approval_key || sessionId);
        setInterruptData({
          actionRequests: pending.action_requests,
          reviewConfigs: pending.review_configs,
          timeoutSeconds: pending.timeout_seconds || null,
          timeoutStartedAt: pending.created_at
            ? pending.created_at * 1000
            : Date.now(),
        });
      } else {
        resetInterruptState();
      }
    },
    [resetInterruptState],
  );

  // Sync share/admin view flags from URL params into context
  useEffect(() => {
    setIsShareView(!!shareId || !!adminViewSessionId);
    setIsAdminView(!!adminViewSessionId);
  }, [shareId, adminViewSessionId]);

  /* Hub disabled - skip hub pending polling
    useEffect(() => {
        if (isInitializing || isShareView || !isAuthenticated()) return;
        if (activeSessionId === HUB_ALIAS) return;
        const interval = setInterval(async () => {
            try {
                const count = await getHubPendingCount();
                setHubPendingCount(count);
            } catch {}
        }, 30000);
        return () => clearInterval(interval);
    }, [isInitializing, isShareView, activeSessionId]);
    */

  useNotificationCount({ setNotificationCount, isInitializing, isShareView });

  const [isStopping, setIsStopping] = useState(false);
  const isStoppingRef = useRef(false);
  const stoppingTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const hasInitialized = useRef(false);
  const isNavigatingToSession = useRef(false); // Flag để tránh double loading
  const isTerminatedByUser = useRef(false); // Add this ref to track user termination
  const prevIsStreamingRef = useRef(false); // Track previous streaming state

  // WebSocket streaming refs
  const wsUpdateAiRef = useRef(null); // updateLatestAiMessage callback for WS events
  const wsPlaceholderIdRef = useRef(null);
  const wsUserMessageRef = useRef(null);
  const wsSessionIdRef = useRef(null);
  const wsStreamDoneRef = useRef(null);
  const lastEventIdRef = useRef(null);
  const [isShareLoading, setIsShareLoading] = useState(false);
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const [settingsDefaultTab, setSettingsDefaultTab] = useState("profile");
  const showScrollDownRef = useRef(false);
  const scrollDownButtonRef = useRef(null);

  // Display mode from server (overrides VITE_CHAT_DISPLAY_MODE)
  const [, setDisplayMode] = useState(config.chatDisplayMode);
  const [brand, setBrand] = useState(null);

  // State for Mode selection - load from localStorage
  const [selectedMode, setSelectedMode] = useState(() => {
    try {
      const savedValue = localStorage.getItem(Storage.chat_mode);
      console.log("Loading Chat Mode from localStorage:", savedValue);
      return savedValue || "auto";
    } catch (error) {
      console.log("Error loading Chat Mode from localStorage:", error);
      return "auto";
    }
  });

  const { suggestions, setSuggestions, showSuggestions, setShowSuggestions } =
    useSuggestions();

  // New state for follow-up questions
  const [, setFollowUpQuestions] = useState([]);

  // New state for file uploads
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const [detectedURLs, setDetectedURLs] = useState([]);
  const [errorLimitInfo, setErrorLimitInfo] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showPromotionPopup, setShowPromotionPopup] = useState(true);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showLimitReached, setShowLimitReached] = useState(false);
  const [isInputExpanded, setIsInputExpanded] = useState(false);

  // Model selection for testing - persist in localStorage
  const [selectedModel, setSelectedModel] = useState(() => {
    try {
      return localStorage.getItem(Storage.chat_model) || "";
    } catch {
      return "";
    }
  });

  // Handler to change model - saves to localStorage IMMEDIATELY (sync) to prevent race conditions
  // when component remounts during navigation between routes
  const handleModelChange = useCallback((newModel) => {
    try {
      if (newModel) {
        localStorage.setItem(Storage.chat_model, newModel);
      } else {
        localStorage.removeItem(Storage.chat_model);
      }
    } catch {
      // Ignore localStorage errors
    }
    setSelectedModel(newModel);
  }, []);

  // Voice chat hook - called when final transcript is received
  // Mirrors sendMessage flow: user message + AI placeholder + loading state
  const handleVoiceTranscript = useCallback(
    (text, confidence) => {
      // Add user's voice input directly to liveMessages as a user message
      // Don't call API because backend already processes via STT → Agent pipeline
      if (text && text.trim()) {
        const timestamp = Date.now();
        const placeholderId = `voice-ai-placeholder-${timestamp}`;

        // Create user message (same as sendMessage)
        const userMessage = {
          id: `voice-${timestamp}`,
          type: "user",
          content: text,
          isVoice: true,
          message_id: null,
        };

        // Create AI placeholder (same as sendMessage)
        const aiPlaceholder = {
          id: placeholderId,
          type: "ai_loading",
          thinkingSteps: [],
          content: "",
          message_id: null,
          isStreaming: true,
          isVoice: true,
        };

        const height = chatContentRef.current?.clientHeight || 0;
        flushSync(() => {
          setPlaceholderHeight(height);
          setLiveMessages([userMessage, aiPlaceholder]);
          setDebugRawEvents([]);
          debugRawEventsRef.current = [];
          setIsLoading(true);
          setIsStreaming(true);
        });
        liveMessagesStartRef.current?.scrollIntoView({
          block: "start",
          behavior: "auto",
        });

        // Auto-rename session if this is the first message of a new session (same as text chat)
        // For voice, we wait for the final transcript (stt_output) to rename
        if (voiceSessionIsNewRef.current && voiceSessionIdRef.current) {
          // Use ref to call handleAutoRename (defined later in component)
          handleAutoRenameRef.current?.(voiceSessionIdRef.current, text);
          // Reset flag so we don't rename again on subsequent messages
          voiceSessionIsNewRef.current = false;
        }
      }
    },
    [isMobile, isAuthenticated],
  );

  // Handle SSE-format events from voice WebSocket (same structure as /v2/chat/stream)
  // Converts JSON events to SSE string format for parseSSEEvents compatibility
  const handleVoiceSSEEvent = useCallback((event) => {
    // Convert JSON event to SSE string format: "event: xxx\ndata: {...}\n\n"
    const eventType = event.type;
    const eventData = { ...event };
    const sseString = `event: ${eventType}\ndata: ${JSON.stringify(eventData)}\n\n`;

    // Add to debugRawEvents (same as regular chat stream)
    setDebugRawEvents((prev) => {
      const newEvents = [...prev, sseString];
      debugRawEventsRef.current = newEvents;
      return newEvents;
    });

    // Parse blocks and update AI message (same as regular stream flow)
    // The rendering will use parseSSEEvents(debugRawEvents) automatically
  }, []);

  // Legacy: Handle text delta directly for backward compatibility
  const handleVoiceAgentResponse = useCallback((text, isFinal) => {
    // This is called alongside SSE events for legacy support
    // The main rendering uses parseSSEEvents, but we keep this for simpler text accumulation
    if (text) {
      setLiveMessages((prev) => {
        // Find the voice AI placeholder
        const voicePlaceholderIndex = prev.findIndex(
          (m) => m.isVoice && m.type !== "user",
        );
        if (voicePlaceholderIndex === -1) return prev;

        const updated = [...prev];
        const placeholder = updated[voicePlaceholderIndex];

        // Only update type if still loading - let parseSSEEvents handle blocks
        if (placeholder.type === "ai_loading") {
          updated[voicePlaceholderIndex] = {
            ...placeholder,
            type: "ai_answer",
            isStreaming: true,
          };
        }
        return updated;
      });
    }
  }, []);

  // Track if the current voice session is new (for auto-rename on first transcript)
  const voiceSessionIsNewRef = useRef(false);
  const voiceSessionIdRef = useRef(null);
  // Ref for handleAutoRename to avoid circular dependency (defined later in component)
  const handleAutoRenameRef = useRef(null);

  // Handle message start from voice WebSocket
  const handleVoiceMessageStart = useCallback(
    (data) => {
      console.log(
        "[VoiceChat] Message start:",
        data.session_id,
        "new:",
        data.is_new_session,
        "from_share:",
        data.is_new_session_from_share,
      );

      // Track if this is a new session for auto-rename on first transcript
      voiceSessionIsNewRef.current =
        data.is_new_session || data.is_new_session_from_share;
      voiceSessionIdRef.current = data.session_id;

      // If new session was created, update activeSessionId
      if (
        (data.is_new_session || data.is_new_session_from_share) &&
        data.session_id
      ) {
        setActiveSessionId(data.session_id);
        localStorage.setItem(Storage.active_session_id, data.session_id);
        syncChatUrl({ sessionId: data.session_id, isEmbedded, buildPath });
      }
    },
    [buildPath, isEmbedded],
  );

  // Handle message end from voice WebSocket
  const handleVoiceMessageEnd = useCallback((data) => {
    console.log("[VoiceChat] Message end, duration:", data.duration_ms, "ms");
    // Note: isLoading/isStreaming are cleared when TTS ends (in useEffect below)
  }, []);

  const handleVoiceError = useCallback((error) => {
    console.error("[VoiceChat] Error:", error);
    showError(error.message || "Voice chat error");
  }, []);

  const {
    isConnected: isVoiceConnected,
    isConnecting: isVoiceConnecting,
    isRecording: isVoiceRecording,
    isSpeaking: isVoiceSpeaking,
    partialTranscript: voicePartialTranscript,
    connect: connectVoice,
    disconnect: disconnectVoice,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
    interrupt: interruptVoice,
    uploadAudioFile: uploadVoiceAudioFile,
  } = useVoiceChat({
    sessionId: activeSessionId || "new",
    apiUrl: API_BASE_URL,
    token: getAccessToken(),
    model: selectedModel, // LLM model (same as /v2/chat/stream)
    language: "auto",
    onTranscript: handleVoiceTranscript,
    onAgentResponse: handleVoiceAgentResponse,
    onSSEEvent: handleVoiceSSEEvent, // SSE-format events (same as /v2/chat/stream)
    onMessageStart: handleVoiceMessageStart, // Message start with session info
    onMessageEnd: handleVoiceMessageEnd, // Message end with duration
    onError: handleVoiceError,
  });

  const {
    isEnabled: isVoiceInputEnabled,
    isInitializing: isVoiceInputInitializing,
    isRecording: isVoiceInputRecording,
    isTranscribing: isVoiceInputTranscribing,
    audioLevel: voiceInputAudioLevel,
    duration: voiceInputDuration,
    partialTranscript: voiceInputPartialTranscript,
    noSpeechError: voiceInputNoSpeechError,
    startRecording: startVoiceInput,
    injectFileAudio: injectVoiceInputFile,
    stopAndTranscribe: stopVoiceInput,
    cancelRecording: cancelVoiceInput,
    retryRecording: retryVoiceInput,
    dismissError: dismissVoiceInputError,
  } = useVoiceInput({
    language: "auto",
    onTranscribed: (text) => {
      sendMessage(text);
    },
    onTranscriptReady: (text) => {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.value = text;
          inputRef.current.focus();
        }
      }, 30);
    },
    onError: (msg) => {
      showError(msg);
    },
  });

  // Track previous speaking state to detect transition from speaking to not speaking
  const prevVoiceSpeakingRef = useRef(false);

  // Handle voice streaming completion - clear loading states when TTS ends
  useEffect(() => {
    const wasSpeaking = prevVoiceSpeakingRef.current;
    const nowSpeaking = isVoiceSpeaking;

    // Detect transition: speaking -> not speaking (TTS playback ended)
    if (wasSpeaking && !nowSpeaking) {
      console.log("[VoiceChat] TTS playback ended, clearing loading states");

      // Clear loading states (same as normal stream end)
      setIsLoading(false);
      setIsStreaming(false);

      // Mark voice AI message as not streaming
      setLiveMessages((prev) => {
        const voiceIndex = prev.findIndex(
          (m) => m.isVoice && m.type !== "user",
        );
        if (voiceIndex === -1) return prev;

        const updated = [...prev];
        updated[voiceIndex] = {
          ...updated[voiceIndex],
          isStreaming: false,
        };
        return updated;
      });
    }

    prevVoiceSpeakingRef.current = nowSpeaking;
  }, [isVoiceSpeaking]);

  const { limitInfo, budgetStatus, checkUsageStatus } = useUsageLimit(
    userId,
    isAuthenticated(),
  );

  useEffect(() => {
    if (limitInfo) {
      setErrorLimitInfo(limitInfo);
    }
  }, [limitInfo]);

  // Auto-expand input on mobile when there are uploaded files
  useEffect(() => {
    if (isMobile && uploadedFiles.length > 0) {
      setIsInputExpanded(true);
    }
  }, [uploadedFiles.length, isMobile]);

  // Auto-focus input when expanded on mobile
  useEffect(() => {
    if (isMobile && isInputExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMobile, isInputExpanded]);

  const showDraftLanding = useMemo(
    () =>
      !sessionId &&
      !shareId &&
      !adminViewSessionId &&
      messages.length === 0 &&
      liveMessages.length === 0 &&
      (isDraftChat || isInitializing),
    [
      sessionId,
      shareId,
      adminViewSessionId,
      messages.length,
      liveMessages.length,
      isDraftChat,
      isInitializing,
    ],
  );

  const { inputSectionRef, inputSectionHeight } =
    useInputSectionMetrics(showDraftLanding);

  // Helper to get current input value from ref
  const getInputValue = useCallback(() => {
    return inputRef.current?.value?.trim() || "";
  }, []);

  const handleSkillInsert = useCallback((skillName) => {
    if (!skillName || !inputRef.current) return;
    const token = `/${skillName} `;
    const current = inputRef.current.value || "";
    inputRef.current.value = current.startsWith(token)
      ? current
      : `${token}${current.replace(/^\/\S+\s*/, "")}`;
    setHasInputText(!!inputRef.current.value);
    inputRef.current.focus?.();
  }, []);

  // Derived state to determine if the specific share loading UI should be shown.
  // This merges the initial app load and the specific share content load into one state for share URLs.
  const isAwaitingContext = !!(
    sourceParam &&
    sourceIdParam &&
    !contextDraft &&
    messages.length === 0
  );
  const shouldShowShareLoader =
    isShareLoading ||
    (isInitializing && (!!shareId || !!adminViewSessionId)) ||
    isAwaitingContext;
  const needsWsConnection = isAuthenticated();
  const isSkeletonShowing =
    isSwitchingSession || (isInitializing && (!!sessionId || !!sourceParam));
  const isInputDisabled =
    isInitializing ||
    isSwitchingSession ||
    (needsWsConnection && !isWsConnected);
  useEffect(() => {
    setIsInputDisabled(isInputDisabled);
  }, [isInputDisabled]);

  const updateScrollDownVisibility = (shouldShow) => {
    if (shouldShow === showScrollDownRef.current) return;
    showScrollDownRef.current = shouldShow;
    const el = scrollDownButtonRef.current;
    if (el) {
      el.style.display = shouldShow ? "" : "none";
    }
  };

  const scrollToBottom = () => {
    // Check if content needs scrolling (has scrollbar)
    const chatContent = chatContentRef.current;
    if (chatContent) {
      const hasScrollbar = chatContent.scrollHeight > chatContent.clientHeight;
      // Only scroll if there's actually content to scroll
      if (hasScrollbar) {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }
      updateScrollDownVisibility(false);
    } else {
      // Fallback if ref not available
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      updateScrollDownVisibility(false);
    }
  };

  useEffect(() => {
    setGreetingMessage(getRandomGreeting());
  }, []);

  const stopAllStreaming = useCallback(
    (clearMessages = false) => {
      if (isStoppingRef.current) onStopConfirmed();
      flushSync(() => {
        setIsStreaming(false);
        setIsLoading(false);
        setDebugRawEvents([]);
        if (clearMessages) setLiveMessages([]);
      });
      chatStream.stopStreaming();
      if (clearMessages) chatStream.loadHistory(messages);
      wsStreamDoneRef.current?.();
      if (contextDraftRef.current) {
        contextDraftRef.current = null;
        setContextDraft(null);
      }
    },
    [chatStream, messages],
  );

  // ========== WebSocket Event Handler ==========
  const handleWsEvent = useCallback(
    (data) => {
      if (data.type === "state_sync") {
        if (data.pending_count !== undefined)
          setHubPendingCount(data.pending_count);
        return;
      }

      if (data.type === "session_renamed") {
        updateSession(data.session_id, { session_name: data.session_name });
        return;
      }

      if (data.type === "session_created" && data.from_share) {
        const newSessionId = data.session_id;
        const sessionTitle =
          data.title ||
          sharedInfo?.title ||
          sharedInfo?.session_name ||
          "New Chat";

        const newSession = {
          session_id: newSessionId,
          session_name: sessionTitle,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          message_count: 1,
        };
        addSession(newSession);
        setActiveSessionId(newSessionId);
        setIsShareView(false);
        setIsDraftChat(false);
        localStorage.setItem(Storage.active_session_id, newSessionId);
        wsSessionIdRef.current = newSessionId;

        apiClient
          .renameSession(newSessionId, userId, sessionTitle)
          .catch(() => {});

        syncChatUrl({ sessionId: newSessionId, isEmbedded, buildPath });
        return;
      }

      if (data.message_type === "context" || contextDraftRef.current?.active) {
        if (data.type === "message_start" && data.message_type === "context") {
          const msgId = `notif-${Date.now()}`;
          contextDraftRef.current = {
            active: true,
            summary: "",
            cta: null,
            source: data.source,
            source_id: data.source_id,
            msgId,
          };
          const notifMsg = {
            role: "assistant",
            type: "chat",
            content: [{ type: "text", text: "" }],
            uuid: msgId,
            isStreaming: true,
          };
          setMessages([notifMsg]);
          chatStream.loadHistory([notifMsg]);
          return;
        }
        if (
          data.type === "content_block_delta" &&
          data.delta?.type === "text_delta"
        ) {
          contextDraftRef.current.summary += data.delta.text;
          const text = contextDraftRef.current.summary;
          const msgId = contextDraftRef.current.msgId;
          setMessages((prev) =>
            prev.map((m) =>
              m.uuid === msgId
                ? { ...m, content: [{ type: "text", text }] }
                : m,
            ),
          );
          chatStream.loadHistory([
            {
              role: "assistant",
              type: "chat",
              content: [{ type: "text", text }],
              uuid: msgId,
              isStreaming: true,
            },
          ]);
          return;
        }
        if (
          data.type === "content_block_start" &&
          data.content_block?.type === "cta"
        ) {
          contextDraftRef.current.cta = {
            intro: data.content_block.intro,
            items: data.content_block.items,
          };
          setContextDraft({ ...contextDraftRef.current });
          return;
        }
        if (data.type === "message_stop") {
          contextDraftRef.current.active = false;
          const text = contextDraftRef.current.summary;
          const msgId = contextDraftRef.current.msgId;
          setMessages((prev) =>
            prev.map((m) =>
              m.uuid === msgId
                ? {
                    ...m,
                    content: [{ type: "text", text }],
                    isStreaming: false,
                  }
                : m,
            ),
          );
          chatStream.loadHistory([
            {
              role: "assistant",
              type: "chat",
              content: [{ type: "text", text }],
              uuid: msgId,
            },
          ]);
          setContextDraft({ ...contextDraftRef.current });
          return;
        }
        if (
          data.type === "content_block_start" ||
          data.type === "content_block_stop"
        ) {
          return;
        }
      }

      let updateAi = wsUpdateAiRef.current;

      if (!updateAi && data.type === "message_start") {
        const placeholderId = `ai-hub-${Date.now()}`;
        const aiPlaceholder = {
          id: placeholderId,
          type: "ai_loading",
          thinkingSteps: [],
          content: "",
          message_id: data.message_id,
          message_type: data.message_type || "chat",
          isStreaming: true,
        };
        setLiveMessages((prev) => [...prev, aiPlaceholder]);
        setIsStreaming(true);
        chatStream.startStreaming();

        const updater = (fn) => {
          setLiveMessages((prev) => {
            const next = [...prev];
            const index = next.findLastIndex((m) => m.id === placeholderId);
            if (index !== -1) next[index] = fn(next[index]);
            return next;
          });
        };
        wsUpdateAiRef.current = updater;
        wsPlaceholderIdRef.current = placeholderId;
        wsStreamDoneRef.current = () => {
          setIsLoading(false);
          setIsStreaming(false);
          updater((msg) => ({ ...msg, isStreaming: false }));
        };
        updateAi = updater;
      }

      if (data.type === "message_stop") {
        stopAllStreaming();
        return;
      }

      if (!updateAi) return;

      const rawStr = `event: ${data.type}\ndata: ${JSON.stringify(data)}`;
      const next = [...debugRawEventsRef.current, rawStr];
      debugRawEventsRef.current = next;
      setDebugRawEvents(next);
      chatStream.appendStreamBlocks(next);

      handleTodoSSEEvent(data, setTodosProgress);
      handleSearchSourceSSEEvent(data, setSearchSources);
      handleWorkspaceFilesSSEEvent(data, setWorkspaceFiles);

      if (
        data.type === "content_block_start" &&
        data.content_block?.type === "approval_request"
      ) {
        setInterruptData(null);
        setInterruptSessionId(
          data.content_block.approval_key || wsSessionIdRef.current,
        );
      }

      if (
        data.type === "content_block_start" &&
        data.content_block?.type === "approval_timeout"
      ) {
        resetInterruptState();
      }

      if (
        data.type === "content_block_delta" &&
        (data.delta?.intent || data.delta?.action_requests)
      ) {
        setInterruptData((prev) => ({
          ...prev,
          intent: data.delta.intent || prev?.intent,
          urgency: data.delta.urgency || prev?.urgency,
          draftResponse: data.delta.draft_response || prev?.draftResponse,
          actionRequests: data.delta.action_requests || prev?.actionRequests,
          reviewConfigs: data.delta.review_configs || prev?.reviewConfigs,
          timeoutSeconds: data.delta.timeout_seconds || prev?.timeoutSeconds,
          timeoutStartedAt: data.delta.timeout_seconds
            ? Date.now()
            : prev?.timeoutStartedAt,
        }));
      }

      if (data.type === "message_start") {
        const messageId = data.message_id;
        if (messageId) {
          const uid = wsUserMessageRef.current?.id;
          const pid = wsPlaceholderIdRef.current;
          setLiveMessages((prev) =>
            prev.map((msg) => {
              if (msg.id === uid || msg.id === pid) {
                return { ...msg, message_id: messageId };
              }
              return msg;
            }),
          );
        }
        if (data.is_new_session_from_share && data.session_id) {
          setActiveSessionId(data.session_id);
          setIsShareView(false);
          localStorage.setItem(Storage.active_session_id, data.session_id);
          syncChatUrl({ sessionId: data.session_id, isEmbedded, buildPath });
        }
        if (data.display_mode) {
          setDisplayMode(data.display_mode);
        }
      } else if (data.type === "task") {
        const taskId = data.id || data.task_id;
        updateAi((msg) => {
          const existingSteps = msg.thinkingSteps || [];
          const existingIndex = existingSteps.findIndex(
            (step) =>
              (step.id && step.id === taskId) ||
              (step.task_id && step.task_id === taskId),
          );
          if (existingIndex !== -1) {
            const updatedSteps = [...existingSteps];
            updatedSteps[existingIndex] = {
              ...updatedSteps[existingIndex],
              ...data,
              updated_at: new Date().toISOString(),
            };
            return {
              ...msg,
              type: "ai_thinking",
              thinkingSteps: updatedSteps,
              isStreaming: false,
            };
          }
          return {
            ...msg,
            type: "ai_thinking",
            thinkingSteps: [...existingSteps, data],
            isStreaming: false,
          };
        });
      } else if (data.type === "task_status_update") {
        updateAi((msg) => {
          const existingSteps = msg.thinkingSteps || [];
          const existingIndex = existingSteps.findIndex(
            (step) =>
              step.id === data.task_id ||
              (step.task_id && step.task_id === data.task_id),
          );
          if (existingIndex !== -1) {
            const updatedSteps = [...existingSteps];
            updatedSteps[existingIndex] = {
              ...updatedSteps[existingIndex],
              status: data.status,
              updated_at: data.timestamp,
            };
            return { ...msg, type: "ai_thinking", thinkingSteps: updatedSteps };
          }
          const newTask = {
            type: "task",
            id: data.task_id,
            task_id: data.task_id,
            content: data.task_name || "Task đang xử lý...",
            status: data.status,
            timestamp: data.timestamp,
            step: "task",
            phase: "start",
          };
          return {
            ...msg,
            type: "ai_thinking",
            thinkingSteps: [...existingSteps, newTask],
          };
        });
      } else if (data.type === "sources") {
        updateAi((msg) => ({
          ...msg,
          type: msg.type === "ai_answer" ? msg.type : "ai_thinking",
          sources: data.sources,
        }));
      } else if (data.type === "search_results") {
        updateAi((msg) => ({
          ...msg,
          type: msg.type === "ai_answer" ? msg.type : "ai_thinking",
          searchResults: data.payload?.results || data.results,
        }));
      } else if (data.type === "answer_delta") {
        updateAi((msg) => ({
          ...msg,
          type: "ai_answer",
          content: (msg.content || "") + data.delta,
          isStreaming: true,
        }));
      } else if (data.type === "answer_complete") {
        updateAi((msg) => ({
          ...msg,
          type: "ai_answer",
          content: data.content,
          isStreaming: false,
        }));
      } else if (data.type === "message_delta") {
        if (data.delta?.type === "text_delta" && data.delta.text) {
          updateAi((msg) => ({
            ...msg,
            type: "ai_answer",
            content: (msg.content || "") + data.delta.text,
            isStreaming: true,
          }));
        } else if (data.delta?.stop_reason) {
          updateAi((msg) => ({
            ...msg,
            isStreaming: false,
            stopReason: data.delta.stop_reason,
          }));
        } else if (data.delta?.status) {
          updateAi((msg) => ({
            ...msg,
            isStreaming: data.delta.status !== "completed",
            stopReason: data.delta.stop_reason,
          }));
        }
      } else if (data.type === "answer") {
        updateAi((msg) => ({
          ...msg,
          type: "ai_answer",
          content: data.content,
          isStreaming: false,
        }));
      } else if (data.type === "follow_up") {
        setFollowUpQuestions(data.questions || []);
      } else if (data.type === "thinking") {
        updateAi((msg) => ({
          ...msg,
          type: "thinking",
          content: (msg.content || "") + data.content,
          isStreaming: true,
          thinkingContent: (msg.thinkingContent || "") + data.content,
        }));
      } else if (data.type === "error") {
        const errorMsg = data.content || data.message || "";
        const isBudgetError =
          errorMsg.includes("giới hạn") || errorMsg.includes("limit");
        if (isBudgetError) {
          setErrorLimitInfo({
            message: errorMsg,
            tier: data.tier,
            deniedBy: data.denied_by,
          });
          setShowPaywall(true);
          setIsLoading(false);
          setIsStreaming(false);
        }
        updateAi((msg) => ({ ...msg, type: "ai_error", content: errorMsg }));
      } else if (data.type === "file_context") {
        updateAi((msg) => ({ ...msg, fileContext: data.files }));
      } else if (data.type === "url_context") {
        updateAi((msg) => ({ ...msg, urlContext: data.urls }));
      } else if (data.type === "progress") {
        updateAi((msg) => {
          const existingEvents = msg.processEvents || [];
          const next = { ...msg, processEvents: [...existingEvents, data] };
          if (next.type === "ai_loading") next.type = "ai_thinking";
          return next;
        });
      }
    },
    [navigate, buildPath],
  );

  const handleWsSubscribed = useCallback((msg) => {
    if (msg.session_id) {
      wsSessionIdRef.current = msg.session_id;
    }
    if (msg.status === "running" && !wsUpdateAiRef.current) {
      const placeholderId = `ai-sub-${Date.now()}`;
      const aiPlaceholder = {
        id: placeholderId,
        type: "ai_loading",
        thinkingSteps: [],
        content: "",
        message_id: null,
        message_type: "chat",
        isStreaming: true,
      };
      setLiveMessages((prev) => [...prev, aiPlaceholder]);
      setIsStreaming(true);
      setIsLoading(true);

      const updater = (fn) => {
        setLiveMessages((prev) => {
          const next = [...prev];
          const index = next.findLastIndex((m) => m.id === placeholderId);
          if (index !== -1) next[index] = fn(next[index]);
          return next;
        });
      };
      wsUpdateAiRef.current = updater;
      wsPlaceholderIdRef.current = placeholderId;
      wsStreamDoneRef.current = () => {
        setIsLoading(false);
        setIsStreaming(false);
        updater((msg) => ({ ...msg, isStreaming: false }));
      };
    }
  }, []);

  const handleWsError = useCallback((err) => {
    if (err.code === "TASK_ALREADY_RUNNING") {
      console.log("[WS] Task already running, subscribing...");
    } else if (
      err.code === "BUDGET_EXCEEDED" ||
      err.code === "LIMIT_EXCEEDED"
    ) {
      const bs = err.budget_status || err.data;
      const denied = bs?.denied_by;

      setTimeout(() => stopAllStreaming(true), 100);

      if (denied?.limit_type === "no_plan") {
        setShowUnavailable(true);
      } else {
        setShowLimitReached(true);
      }

      checkUsageStatus();
    } else {
      console.warn("[WS] Error:", err.code, err.message);
    }
  }, []);

  const handleWsStateChange = useCallback((state) => {
    setIsWsConnected(state === "connected");
  }, []);

  const agentWs = useAgentWebSocket({
    onEvent: handleWsEvent,
    onSubscribed: handleWsSubscribed,
    onError: handleWsError,
    onStateChange: handleWsStateChange,
    enabled: isAuthenticated() && !!currentUser,
  });

  const fetchContextSentRef = useRef(null);
  useEffect(() => {
    if (
      !isInitializing &&
      isWsConnected &&
      sourceParam &&
      sourceIdParam &&
      !contextDraft &&
      fetchContextSentRef.current !== sourceIdParam
    ) {
      fetchContextSentRef.current = sourceIdParam;
      setIsDraftChat(true);
      agentWs.send({
        type: "fetch_context",
        source: sourceParam,
        source_id: sourceIdParam,
      });
    }
  }, [isInitializing, isWsConnected, sourceParam, sourceIdParam]);

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setIsLoading(false);
    setPlaceholderHeight(0);

    // Find the AI message placeholder ID from the current live messages
    const aiMessage = liveMessages.find((m) => m.type !== "user");
    const placeholderId = aiMessage ? aiMessage.id : null;

    // Update live messages to reflect termination
    // Mark any in-progress thinking steps and timeline steps/items as 'stopped'
    setLiveMessages((prev) =>
      prev.map((msg) => {
        if (msg.type === "user") return msg;

        const updatedThinkingSteps = (msg.thinkingSteps || []).map((step) => {
          if (
            step.status === "in_progress" ||
            step.status === "queued" ||
            step.status === "queue" ||
            step.status === "pending"
          ) {
            return { ...step, status: "stopped" };
          }
          return step;
        });

        // Transform process events to mark active steps/items as stopped
        const events = Array.isArray(msg.processEvents)
          ? msg.processEvents.slice()
          : [];
        if (events.length > 0) {
          // Build a lightweight step map to find active steps and their items
          const stepMap = new Map();
          for (const ev of events) {
            if (!ev || ev.type !== "progress") continue;
            const { action, payload } = ev;
            if (!payload && action !== "init") continue;
            if (action === "step.start") {
              const s = stepMap.get(payload.step_id) || {
                id: payload.step_id,
                title: payload.title,
                status: payload.status || "in_progress",
                items: [],
              };
              s.title = payload.title ?? s.title;
              s.status = payload.status || s.status;
              stepMap.set(payload.step_id, s);
            } else if (action === "step.update") {
              const s = stepMap.get(payload.step_id);
              if (s && payload.fields?.status) s.status = payload.fields.status;
            } else if (action === "step.complete") {
              const s = stepMap.get(payload.step_id);
              if (s) s.status = payload.status || "done";
            } else if (action === "item.append") {
              const s = stepMap.get(payload.step_id);
              if (s && payload.item) {
                const item = payload.item;
                s.items.push({ id: item.id, status: item.status });
              }
            } else if (action === "item.update") {
              const s = stepMap.get(payload.step_id);
              if (s) {
                const it = s.items.find((i) => i.id === payload.item_id);
                if (it && payload.fields?.status)
                  it.status = payload.fields.status;
              }
            }
          }

          // Append updates to stop active steps
          for (const [stepId, s] of stepMap.entries()) {
            if (
              s.status === "in_progress" ||
              s.status === "queued" ||
              s.status === "queue" ||
              s.status === "pending"
            ) {
              events.push({
                type: "progress",
                action: "step.update",
                payload: { step_id: stepId, fields: { status: "stopped" } },
                timestamp: new Date().toISOString(),
              });
            }
            // Stop active/queued items under this step
            for (const it of s.items) {
              if (
                it.status === "in_progress" ||
                it.status === "queued" ||
                it.status === "queue" ||
                it.status === "pending" ||
                typeof it.status === "undefined"
              ) {
                events.push({
                  type: "progress",
                  action: "item.update",
                  payload: {
                    step_id: stepId,
                    item_id: it.id,
                    fields: { status: "stopped" },
                  },
                  timestamp: new Date().toISOString(),
                });
              }
            }
          }

          // Optionally mark timeline ended to prevent further animations
          events.push({
            type: "progress",
            action: "step.complete",
            payload: {
              step_id: "__termination__",
              status: "stopped",
              end: true,
            },
            timestamp: new Date().toISOString(),
          });
        }

        return {
          ...msg,
          type: "ai_answer_terminated",
          isStreaming: false,
          thinkingSteps: updatedThinkingSteps,
          currentProcess: null,
          processEvents: events,
        };
      }),
    );
  };

  const onStopConfirmed = useCallback(() => {
    if (stoppingTimeoutRef.current) {
      clearTimeout(stoppingTimeoutRef.current);
      stoppingTimeoutRef.current = null;
    }
    stopStreaming();
    setIsStopping(false);
    isStoppingRef.current = false;
  }, [stopStreaming]);

  const handleTerminate = async () => {
    isTerminatedByUser.current = true;
    setIsStopping(true);
    isStoppingRef.current = true;
    setInterruptSessionId(null);
    setInterruptData(null);

    stoppingTimeoutRef.current = setTimeout(() => {
      stopStreaming();
      setIsStopping(false);
      isStoppingRef.current = false;
    }, 30000);

    if (!activeSessionId) {
      stopStreaming();
      setIsStopping(false);
      isStoppingRef.current = false;
      clearTimeout(stoppingTimeoutRef.current);
      return;
    }

    try {
      agentWs.stop(activeSessionId);
    } catch (error) {
      stopStreaming();
      setIsStopping(false);
      isStoppingRef.current = false;
      clearTimeout(stoppingTimeoutRef.current);
    }
  };

  useSidebarPersistence({ isSidebarOpen, isMobile });

  // Only auto-scroll when new messages are added (not when switching sessions)
  const prevMessagesLength = useRef(0);
  useEffect(() => {
    // Only scroll if messages were added (not replaced)
    if (messages.length > prevMessagesLength.current) {
      scrollToBottom();
    }
    prevMessagesLength.current = messages.length;
  }, [messages]);

  // IntersectionObserver to detect when bottom is visible
  // This is more reliable than scroll events for detecting bottom position
  useEffect(() => {
    const bottomElement = messagesEndRef.current;
    const chatContent = chatContentRef.current;

    if (!bottomElement || !chatContent) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          // Bottom is visible, hide the scroll down button
          updateScrollDownVisibility(false);
        }
        // Note: We don't set it to true when not intersecting
        // because scroll event handler will handle showing the button
      },
      {
        root: chatContent,
        threshold: 0.5, // Trigger when at least 50% of bottom element is visible (more strict)
        rootMargin: "0px", // No margin - only hide when truly at bottom
      },
    );

    observer.observe(bottomElement);

    return () => {
      observer.disconnect();
    };
  }, []); // messagesEndRef is stable - no need to recreate observer on message changes

  // Scroll down button visibility & auto-hide scrollbar
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent) return;

    let scrollTimeout;
    let rafId = null;
    let resizeDebounceTimeout;
    let lastUserScrollTime = 0;

    const SCROLL_THRESHOLD = 150;
    const USER_SCROLL_GRACE_PERIOD = 300;

    const checkScrollPosition = (source = "unknown") => {
      const { scrollTop, scrollHeight, clientHeight } = chatContent;
      const hasScrollbar = scrollHeight > clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (
        source === "resize" &&
        Date.now() - lastUserScrollTime < USER_SCROLL_GRACE_PERIOD
      ) {
        return;
      }

      updateScrollDownVisibility(
        hasScrollbar && distanceFromBottom > SCROLL_THRESHOLD,
      );
    };

    const handleScroll = () => {
      lastUserScrollTime = Date.now();
      chatContent.classList.add("is-scrolling");

      // Use rAF to coalesce scroll checks with the paint cycle
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          checkScrollPosition("scroll");
        });
      }

      // Hide scrollbar after 1 second of no scrolling
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        chatContent.classList.remove("is-scrolling");
      }, 1000);
    };

    chatContent.addEventListener("scroll", handleScroll, { passive: true });

    // ResizeObserver to handle content size changes (e.g., during streaming)
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeDebounceTimeout);
      resizeDebounceTimeout = setTimeout(() => {
        checkScrollPosition("resize");
      }, 250);
    });
    resizeObserver.observe(chatContent);

    // Initial check
    checkScrollPosition("init");

    return () => {
      chatContent.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      clearTimeout(scrollTimeout);
      if (rafId !== null) cancelAnimationFrame(rafId);
      clearTimeout(resizeDebounceTimeout);
    };
  }, []);

  // Update streaming state ref when isStreaming changes (used elsewhere)
  useEffect(() => {
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // Helper: Parse V2 history format (role-based messages with content blocks)
  // Output format compatible with MessageBlockRenderer
  const parseV2History = (historyData, sessionId) => {
    const messages = historyData?.messages || [];
    const formattedMessages = [];
    let messageIndex = 0;

    const parseAssistantBlocks = (msg) => {
      const rawContentBlocks = Array.isArray(msg.content) ? msg.content : [];
      const blocks = [];

      rawContentBlocks.forEach((block) => {
        if (block.type === "thinking") {
          blocks.push({
            type: "thinking",
            thinking: block.thinking || "",
          });
        } else if (block.type === "text") {
          const extras = block.extras;
          if (extras?.block_subtype === "user_stopped") {
            blocks.push({
              type: "terminal_user_stopped",
              content: block.text || "",
              extras,
            });
          } else if (extras?.block_subtype === "error") {
            blocks.push({
              type: "terminal_error",
              content: block.text || "",
              extras,
            });
          } else {
            blocks.push({ type: "text", content: block.text || "" });
          }
        } else if (block.type === "tool_use") {
          const { tool_content_message, ...cleanInput } = block.input || {};
          blocks.push({
            type: "tool_use",
            id: block.id,
            name: block.name,
            input: cleanInput,
            tool_content_message:
              tool_content_message ||
              buildFallbackToolName(block.name, cleanInput),
          });
        } else if (block.type === "approval_request") {
          blocks.push(block);
        }
      });

      if (msg.tool_calls && msg.tool_calls.length > 0) {
        msg.tool_calls.forEach((tc) => {
          const exists = blocks.some(
            (b) => b.type === "tool_use" && b.id === tc.id,
          );
          if (!exists) {
            const { tool_content_message: _tcm, ...cleanArgs } = tc.args || {};
            blocks.push({
              type: "tool_use",
              id: tc.id,
              name: tc.name,
              input: cleanArgs,
              tool_content_message:
                tc.tool_content_message ||
                buildFallbackToolName(tc.name, cleanArgs),
            });
          }
        });
      }

      if (msg.attachments && msg.attachments.length > 0) {
        blocks.push({ type: "attachments", files: msg.attachments });
      }

      return blocks;
    };

    const parseToolBlocks = (msg) => {
      const toolResultBlock = {
        type: "tool_result",
        tool_use_id: msg.tool_call_id,
        name: msg.name,
        content: msg.content,
        status: msg.status || "success",
      };
      if (msg.artifact) {
        toolResultBlock.artifact = msg.artifact;
      }
      return [toolResultBlock];
    };

    let pendingGroup = null;

    const flushGroup = (isDone = true) => {
      if (!pendingGroup) return;
      const mergedBlocks = mergeToolBlocks(pendingGroup.blocks);
      formattedMessages.push({
        id: `${sessionId}-history-${messageIndex++}`,
        type: "ai_group",
        isStreaming: false,
        isDone,
        summary: pendingGroup.summary,
        blocks: mergedBlocks,
      });
      pendingGroup = null;
    };

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const displayType = msg.display_type;

      if (msg.role === "user") {
        flushGroup();
        let textContent = "";
        const attachments = [];

        if (typeof msg.content === "string") {
          textContent = msg.content;
        } else if (Array.isArray(msg.content)) {
          msg.content.forEach((block) => {
            if (block.type === "text" && block.text) {
              textContent += (textContent ? "\n" : "") + block.text;
            } else if (block.type === "attachment") {
              attachments.push(block);
            }
          });
        }

        formattedMessages.push({
          id: `${sessionId}-history-${messageIndex++}`,
          type: "user",
          content: textContent,
          attachments: attachments.length > 0 ? attachments : undefined,
          isStreaming: false,
        });
        continue;
      }

      if (displayType === "content") {
        flushGroup();
        const blocks =
          msg.role === "assistant"
            ? parseAssistantBlocks(msg)
            : parseToolBlocks(msg);
        const mergedBlocks = mergeToolBlocks(blocks);
        formattedMessages.push({
          id: `${sessionId}-history-${messageIndex++}`,
          type: "ai_answer",
          isStreaming: false,
          message_type: msg.message_type || "step",
          blocks: mergedBlocks,
        });
        continue;
      }

      if (
        displayType === "group_start" ||
        displayType === "group_item" ||
        displayType === "group_end"
      ) {
        const blocks =
          msg.role === "assistant"
            ? parseAssistantBlocks(msg)
            : parseToolBlocks(msg);

        if (displayType === "group_start") {
          flushGroup();
          pendingGroup = { summary: msg.summary || "", blocks: [...blocks] };
        } else if (displayType === "group_item") {
          if (!pendingGroup) {
            pendingGroup = { summary: "", blocks: [] };
          }
          pendingGroup.blocks.push(...blocks);
        } else if (displayType === "group_end") {
          if (!pendingGroup) {
            pendingGroup = { summary: "", blocks: [] };
          }
          pendingGroup.blocks.push(...blocks);
          if (msg.summary) {
            pendingGroup.summary = msg.summary;
          }
          flushGroup();
        }
        continue;
      }

      // Fallback: no display_type (backward compat with old backend)
      const blocks =
        msg.role === "assistant"
          ? parseAssistantBlocks(msg)
          : parseToolBlocks(msg);

      // Look ahead for tool results (old behavior)
      if (msg.role === "assistant") {
        let j = i + 1;
        while (
          j < messages.length &&
          messages[j].role === "tool" &&
          !messages[j].display_type
        ) {
          blocks.push(...parseToolBlocks(messages[j]));
          j++;
        }
        i = j - 1;
      }

      const mergedBlocks = mergeToolBlocks(blocks);
      formattedMessages.push({
        id: `${sessionId}-history-${messageIndex++}`,
        type: "ai_answer",
        isStreaming: false,
        message_type: msg.message_type || "step",
        blocks: mergedBlocks,
      });
    }

    const isRunning = historyData?.agent_status === "running";
    flushGroup(!isRunning);
    return formattedMessages;
  };

  const reconnectRunningSession = useCallback((sessionId, historyData) => {
    if (historyData.agent_status !== "running") return;

    const placeholderId = `ai-placeholder-${Date.now()}`;
    const aiPlaceholder = {
      id: placeholderId,
      type: "ai_loading",
      thinkingSteps: [],
      content: "",
      message_id: null,
      message_type: "chat",
      isStreaming: true,
    };
    setLiveMessages([aiPlaceholder]);
    setIsStreaming(true);

    const updateLatestAiMessage = (updater) => {
      setLiveMessages((prev) => {
        const next = [...prev];
        const index = next.findLastIndex((m) => m.id === placeholderId);
        if (index !== -1) {
          next[index] = updater(next[index]);
        }
        return next;
      });
    };

    wsUpdateAiRef.current = updateLatestAiMessage;
    wsPlaceholderIdRef.current = placeholderId;
    wsSessionIdRef.current = sessionId;
    wsStreamDoneRef.current = () => {
      setIsLoading(false);
      setIsStreaming(false);
      updateLatestAiMessage((msg) => ({ ...msg, isStreaming: false }));
    };
  }, []);

  // Fetch history for a session (chỉ dùng cho initial load)
  const fetchHistory = useCallback(
    async (sessionId) => {
      if (!sessionId) return;
      setIsLoading(true);
      let historyData = null;
      try {
        if (!isAuthenticated()) throw new Error("Unauthorized");

        historyData = await apiClient.fetchHistoryV2(sessionId);
        if (DEBUG_ENABLED) {
          console.log(
            "[DEBUG] fetchHistory - Using V2 API, raw historyData:",
            historyData,
          );
        }

        if (DEBUG_ENABLED) {
          setDebugRawHistory(historyData);
        }

        const formattedMessages = parseV2History(historyData, sessionId);

        restoreInterruptFromHistory(historyData, sessionId);
        setBrand(historyData.brand || null);
        lastEventIdRef.current = historyData.last_event_id || null;

        setMessages(formattedMessages);
        chatStream.loadHistory(historyData.messages || []);
        if (historyData.agent_status === "running") {
          chatStream.startStreaming();
        }
        const lastMessage = formattedMessages[formattedMessages.length - 1];
        if (
          lastMessage &&
          lastMessage.type === "ai_answer" &&
          lastMessage.additional_kwargs?.follow_up_questions?.length > 0
        ) {
          setFollowUpQuestions(
            lastMessage.additional_kwargs.follow_up_questions,
          );
        } else {
          setFollowUpQuestions([]);
        }
        const restoredTodos = historyData?.workspace?.todos || null;
        setTodosProgress(restoredTodos);
        const restoredSources = historyData?.workspace?.sources || null;
        setSearchSources(restoredSources);
        const restoredFiles = historyData?.workspace?.workspace_files || null;
        setWorkspaceFiles(restoredFiles);
      } catch (error) {
        console.error("Error fetching history:", error);
        showError("Không thể tải lịch sử cuộc trò chuyện. Vui lòng thử lại.");
        setMessages([]);
        chatStream.reset();
        chatStream.reset();
      } finally {
        setIsLoading(false);
        setPlaceholderHeight(0);
      }

      if (historyData) {
        reconnectRunningSession(sessionId, historyData);
      }
      if (!historyData || historyData.agent_status !== "running") {
        setLiveMessages([]);
      }
    },
    [userId, reconnectRunningSession, chatStream],
  );

  // Load session data without loading spinner (cho switch session)
  const loadSessionData = useCallback(
    async (sessionId) => {
      if (!sessionId) return;
      try {
        if (!isAuthenticated()) throw new Error("Unauthorized");

        // Use V2 API only
        const historyData = await apiClient.fetchHistoryV2(sessionId);
        if (DEBUG_ENABLED) {
          console.log(
            "[DEBUG] loadSessionData - Using V2 API, raw historyData:",
            historyData,
          );
        }

        // Debug: save raw history data
        if (DEBUG_ENABLED) {
          setDebugRawHistory(historyData);
        }

        // Parse V2 format (role-based messages with content blocks)
        const formattedMessages = parseV2History(historyData, sessionId);

        restoreInterruptFromHistory(historyData, sessionId);
        setBrand(historyData.brand || null);

        setMessages(formattedMessages);
        chatStream.loadHistory(historyData.messages || []);
        if (historyData.agent_status === "running") {
          chatStream.startStreaming();
        }
        const lastMessage = formattedMessages[formattedMessages.length - 1];
        if (
          lastMessage &&
          lastMessage.type === "ai_answer" &&
          lastMessage.additional_kwargs?.follow_up_questions?.length > 0
        ) {
          setFollowUpQuestions(
            lastMessage.additional_kwargs.follow_up_questions,
          );
        } else {
          setFollowUpQuestions([]);
        }
        setPlaceholderHeight(0);

        const restoredTodos = historyData?.workspace?.todos || null;
        setTodosProgress(restoredTodos);
        const restoredSources = historyData?.workspace?.sources || null;
        setSearchSources(restoredSources);
        const restoredFiles = historyData?.workspace?.workspace_files || null;
        setWorkspaceFiles(restoredFiles);

        reconnectRunningSession(sessionId, historyData);
        if (historyData.agent_status !== "running") {
          setLiveMessages([]);
        }
        getAgentWS().subscribe(sessionId, historyData?.last_event_id || null);
      } catch (error) {
        console.error("Error loading session data:", error);
        showError("Không thể tải dữ liệu phiên. Vui lòng thử lại.");
        setMessages([]);
        chatStream.reset();
      }
    },
    [userId],
  );

  // Create new chat (draft mode)
  const ensureAuthOrOpenLogin = () => {
    if (isAuthenticated()) return true;
    const currentPath = location.pathname + location.search;
    navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
    return false;
  };

  const handleNewChat = useCallback(() => {
    if (isLoading || isInitializing) return;
    if (!ensureAuthOrOpenLogin()) return;

    isNavigatingToSession.current = true;

    setIsShareView(false);
    setIsDraftChat(true);
    setActiveSessionId(null);
    setMessages([]);
    chatStream.reset();
    localStorage.removeItem(Storage.active_session_id);
    setLiveMessages([]);
    setPlaceholderHeight(0);
    setFollowUpQuestions([]);
    setUploadedFiles([]);
    setDetectedURLs([]);
    setTodosProgress(null);
    setSearchSources(null);
    setWorkspaceFiles(null);
    resetInterruptState();

    if (!isEmbedded) {
      const isChatRoute =
        window.location.pathname === "/" ||
        window.location.pathname.startsWith("/chat");
      if (sessionId || isShareView || !isChatRoute) {
        navigate(buildPath("/"), { replace: true });
      }
    }

    setTimeout(() => {
      isNavigatingToSession.current = false;
    }, 10);
  }, [
    isLoading,
    isInitializing,
    sessionId,
    navigate,
    buildPath,
    isShareView,
    isEmbedded,
  ]);

  const switchSession = useCallback(
    async (sessionId) => {
      if (sessionId === activeSessionId && messages.length > 0) {
        return;
      }

      isNavigatingToSession.current = true;
      setIsSwitchingSession(true);

      const isHub = sessionId === HUB_ALIAS || sessionId === HUB_ALIAS;
      if (!isEmbedded) {
        const urlPath = isHub ? `/chat/${HUB_ALIAS}` : `/chat/${sessionId}`;
        navigate(buildPath(urlPath), { replace: true });
      }

      if (isHub) {
        setHubPendingCount(0);
      }
      setIsShareView(false);
      setIsDraftChat(false);
      setActiveSessionId(sessionId);
      setContextDraft(null);
      contextDraftRef.current = null;
      fetchContextSentRef.current = null;
      localStorage.setItem(Storage.active_session_id, sessionId);

      setIsMessagesVisible(false);

      setMessages([]);
      chatStream.reset();
      setLiveMessages([]);
      setFollowUpQuestions([]);
      setPlaceholderHeight(0);
      updateScrollDownVisibility(false);
      setDebugRawEvents([]);
      debugRawEventsRef.current = [];
      setTodosProgress(null);
      setSearchSources(null);
      setWorkspaceFiles(null);
      setIsStreaming(false);
      setIsLoading(false);
      wsUpdateAiRef.current = null;
      wsPlaceholderIdRef.current = null;
      wsStreamDoneRef.current = null;
      resetInterruptState();

      try {
        if (sessionId) {
          await loadSessionData(sessionId);

          setTimeout(() => {
            scrollToBottom();
            setIsMessagesVisible(true);
          }, 2);
        }
      } finally {
        setIsSwitchingSession(false);
        setTimeout(() => {
          isNavigatingToSession.current = false;
        }, 2);
      }
    },
    [activeSessionId, userId, navigate, buildPath, loadSessionData, isEmbedded],
  );

  useEffect(() => {
    if (!loadRequest.pendingSessionId) return;
    const target = loadRequest.pendingSessionId;
    consumeLoad();
    if (target === activeSessionIdRef.current) {
      loadSessionData(target).catch((e) =>
        console.error("Failed to load session:", e),
      );
      return;
    }
    switchSession(target);
  }, [
    loadRequest.requestId,
    loadRequest.pendingSessionId,
    consumeLoad,
    loadSessionData,
  ]);

  useEffect(() => {
    if (sessionsStatus !== "succeeded") return;
    if (
      !isInitializing &&
      activeSessionId &&
      activeSessionId !== HUB_ALIAS &&
      !sessions.some((s) => s.session_id === activeSessionId)
    ) {
      handleNewChat();
    }
  }, [
    sessions,
    activeSessionId,
    isInitializing,
    handleNewChat,
    sessionsStatus,
  ]);

  useEffect(() => {
    if (hasInitialized.current) return;

    const currentUserId = getUserId();
    const currentSessionId = sessionId;
    const currentShareId = shareId;
    const currentAdminViewSessionId = adminViewSessionId;

    const initializeApp = async () => {
      hasInitialized.current = true;

      if (currentAdminViewSessionId) {
        setIsShareView(true);
        setIsAdminView(true);
        setIsDraftChat(false);
        setIsShareLoading(true);
        try {
          const data = await adminApi.getSessionView(currentAdminViewSessionId);
          if (!data) throw new Error("Session not found.");
          setSharedInfo(data.session_info);
          const formatted = parseV2History(data, currentAdminViewSessionId);
          setMessages(formatted);
          chatStream.loadHistory(data.messages || []);
          const lastMessage = formatted[formatted.length - 1];
          if (
            lastMessage &&
            lastMessage.type === "ai_answer" &&
            lastMessage.additional_kwargs?.follow_up_questions?.length > 0
          ) {
            setFollowUpQuestions(
              lastMessage.additional_kwargs.follow_up_questions,
            );
          } else {
            setFollowUpQuestions([]);
          }
        } catch (err) {
          console.error("Error fetching admin view session:", err);
          showError("Không thể tải session. Vui lòng kiểm tra quyền admin.");
          setMessages([]);
          chatStream.reset();
        } finally {
          setIsShareLoading(false);
        }
      } else if (currentShareId) {
        setIsShareView(true);
        setIsDraftChat(false);
        setIsShareLoading(true);
        try {
          const data = await shareApi.getSharedContent(currentShareId);
          if (!data) throw new Error("Shared content not found or expired.");
          setSharedInfo(data.share_info);
          const formatted = parseV2History(data, currentShareId);
          setMessages(formatted);
          chatStream.loadHistory(data.messages || []);
          const lastMessage = formatted[formatted.length - 1];
          if (
            lastMessage &&
            lastMessage.type === "ai_answer" &&
            lastMessage.additional_kwargs?.follow_up_questions?.length > 0
          ) {
            setFollowUpQuestions(
              lastMessage.additional_kwargs.follow_up_questions,
            );
          } else {
            setFollowUpQuestions([]);
          }
        } catch (err) {
          console.error("Error fetching shared content:", err);
          showError("Không thể tải nội dung chia sẻ. Vui lòng thử lại.");
          setMessages([]);
          chatStream.reset();
        } finally {
          setIsShareLoading(false);
        }
      } else if (currentSessionId && isAuthenticated() && currentUserId) {
        setIsSwitchingSession(true);
        setIsMessagesVisible(false);
        setIsDraftChat(false);
        setActiveSessionId(currentSessionId);
        try {
          await fetchHistory(currentSessionId);
          getAgentWS().subscribe(currentSessionId, lastEventIdRef.current);
        } finally {
          setIsSwitchingSession(false);
          setTimeout(() => {
            scrollToBottom();
            setIsMessagesVisible(true);
          }, 2);
        }
      } else {
        // --- Case 3: Start new draft ---
        setIsDraftChat(true);
      }

      setIsInitializing(false);
    };

    initializeApp();
  }, [isInitializing]); // ⚠️ Empty dependency array - only run once on mount

  // Handle URL changes after initial load (for session switching)
  useEffect(() => {
    if (!hasInitialized.current || isInitializingRef.current) return; // Wait for initial load to complete

    // Skip nếu đang navigate từ switchSession function để tránh double loading
    if (isNavigatingToSession.current) return;

    const handleURLChange = async () => {
      if (adminViewSessionId) {
        setIsShareView(true);
        setIsAdminView(true);
        setIsDraftChat(false);
        setMessages([]);
        chatStream.reset();
        setIsShareLoading(true);
        try {
          const data = await adminApi.getSessionView(adminViewSessionId);
          if (!data) throw new Error("Session not found.");
          setSharedInfo(data.session_info);
          const formatted = parseV2History(data, adminViewSessionId);
          setMessages(formatted);
          chatStream.loadHistory(data.messages || []);
          const lastMessage = formatted[formatted.length - 1];
          if (
            lastMessage &&
            lastMessage.type === "ai_answer" &&
            lastMessage.additional_kwargs?.follow_up_questions?.length > 0
          ) {
            setFollowUpQuestions(
              lastMessage.additional_kwargs.follow_up_questions,
            );
          } else {
            setFollowUpQuestions([]);
          }
        } catch (err) {
          console.error("Error fetching admin view session:", err);
          showError("Không thể tải session. Vui lòng kiểm tra quyền admin.");
          setMessages([]);
          chatStream.reset();
        } finally {
          setIsShareLoading(false);
        }
      } else if (shareId) {
        // Handle share URL
        setIsShareView(true);
        setIsDraftChat(false);
        setMessages([]);
        chatStream.reset(); // Clear previous messages
        setIsShareLoading(true);
        try {
          const data = await shareApi.getSharedContent(shareId);
          if (!data) throw new Error("Shared content not found or expired.");
          setSharedInfo(data.share_info);

          const formatted = parseV2History(data, shareId);

          setMessages(formatted);
          chatStream.loadHistory(data.messages || []);
          const lastMessage = formatted[formatted.length - 1];
          if (
            lastMessage &&
            lastMessage.type === "ai_answer" &&
            lastMessage.additional_kwargs?.follow_up_questions?.length > 0
          ) {
            setFollowUpQuestions(
              lastMessage.additional_kwargs.follow_up_questions,
            );
          } else {
            setFollowUpQuestions([]);
          }
        } catch (err) {
          console.error("Error fetching shared content:", err);
          showError("Không thể tải nội dung chia sẻ. Vui lòng thử lại.");
          setMessages([]);
          chatStream.reset();
        } finally {
          setIsShareLoading(false);
        }
      } else if (urlSessionId && isAuthenticated() && userId) {
        if (urlSessionId !== activeSessionIdRef.current) {
          setMessages([]);
          chatStream.reset();
          setLiveMessages([]);
          setFollowUpQuestions([]);
          setDebugRawEvents([]);
          debugRawEventsRef.current = [];

          setIsDraftChat(false);
          setActiveSessionId(urlSessionId);
          localStorage.setItem(Storage.active_session_id, urlSessionId);

          try {
            await loadSessionData(urlSessionId);
          } catch (e) {
            console.error("Failed to load session:", e);
            if (!isEmbedded) navigate(buildPath("/chat"), { replace: true });
          }
        }
      } else if (
        !urlSessionId &&
        !shareId &&
        !adminViewSessionId &&
        !activeSessionIdRef.current
      ) {
        setIsShareView(false);
        setIsAdminView(false);
        setIsDraftChat(true);
      }
    };

    handleURLChange();
  }, [
    urlSessionId,
    shareId,
    adminViewSessionId,
    userId,
    navigate,
    buildPath,
    loadSessionData,
    isEmbedded,
  ]);

  // Enforce redirect when unauthenticated and on /chat or /chat/:id; also stop loading
  useEffect(() => {
    if (!isAuthenticated()) {
      setIsInitializing(false);
      setIsDraftChat(true);
      setActiveSessionId(null);
      setMessages([]);
      chatStream.reset();
      localStorage.removeItem(Storage.active_session_id);
      setLiveMessages([]);
      setPlaceholderHeight(0);
      setFollowUpQuestions([]);
      setUploadedFiles([]); // Clear uploaded files when unauthenticated
      setDetectedURLs([]); // Clear detected URLs when unauthenticated
      if (
        !shareId &&
        (sessionId ||
          adminViewSessionId ||
          location.pathname.startsWith("/chat") ||
          location.pathname.startsWith("/admin-view"))
      ) {
        navigate("/", { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, shareId, adminViewSessionId, location.pathname]);

  // handleAutoRenameRef no longer needed — backend handles auto-rename via WebSocket
  useEffect(() => {
    handleAutoRenameRef.current = null;
  }, []);

  // =====================================================
  // V2 API: Approval Handlers for HITL
  // =====================================================

  const handleApprove = async (options) => {
    if (!interruptSessionId) return;
    setIsSubmittingApproval(true);
    try {
      const wsPayload = {
        sessionId: activeSessionId || wsSessionIdRef.current,
        approvalKey: interruptSessionId,
      };

      if (options?.answers) {
        wsPayload.answers = options.answers;
        if (options.declined) {
          wsPayload.declined = true;
        }
      } else {
        wsPayload.decisions = [{ type: "approve" }];
        if (options?.userEditContent) {
          wsPayload.decisions[0].userEditContent = options.userEditContent;
        }
      }

      agentWs.sendApproval(wsPayload);
    } catch (error) {
      console.error("[WS] Approval failed:", error);
    } finally {
      setIsSubmittingApproval(false);
      resetInterruptState();
    }
  };

  const handleReject = async () => {
    if (!interruptSessionId) return;
    setIsSubmittingApproval(true);
    try {
      agentWs.sendApproval({
        sessionId: activeSessionId || wsSessionIdRef.current,
        approvalKey: interruptSessionId,
        decisions: [{ type: "reject" }],
      });
    } catch (error) {
      console.error("[WS] Rejection failed:", error);
    } finally {
      setIsSubmittingApproval(false);
      resetInterruptState();
    }
  };

  const sendMessage = async (
    messageContent,
    mode = null,
    isAutoSuggest = false,
  ) => {
    const userMessageContent = messageContent.trim();
    if (
      !userMessageContent &&
      uploadedFiles.length === 0 &&
      detectedURLs.length === 0
    )
      return;
    if (isInitializing) return;

    if (isLoading && !isStreaming) return;
    if (!isAuthenticated()) {
      ensureAuthOrOpenLogin();
      return;
    }

    if (budgetStatus && !budgetStatus.is_allowed) {
      if (budgetStatus.denied_by?.limit_type === "no_plan") {
        setShowUnavailable(true);
      } else {
        setShowLimitReached(true);
      }
      return;
    }

    if (isStreaming && activeSessionId) {
      const pushRaw = `event: push_user_message\ndata: ${JSON.stringify({ type: "push_user_message", content: userMessageContent })}`;
      setDebugRawEvents((prev) => {
        const next = [...prev, pushRaw];
        debugRawEventsRef.current = next;
        return next;
      });
      agentWs.pushMessage(activeSessionId, userMessageContent);
      return;
    }

    const uploadedContentUrls = uploadedFiles
      .filter((f) => f.status === "completed" && f.contentUrl)
      .map((f) => f.contentUrl);

    const messageUrls = extractUniqueURLs(messageContent);
    const contentUrls = [...uploadedContentUrls, ...messageUrls];

    if (uploadedFiles.some((f) => f.status === "uploading")) {
      showError("Vui lòng đợi tất cả file upload xong.");
      return;
    }

    // Clear uploaded files immediately after capturing URLs (before streaming starts)
    if (uploadedFiles.length > 0) {
      uploadedFiles.forEach((f) => {
        if (f.previewUrl) {
          URL.revokeObjectURL(f.previewUrl);
        }
      });
      setUploadedFiles([]);
    }

    setFollowUpQuestions([]);

    if (liveMessages.length > 0) {
      // Convert liveMessages to V2 blocks format before merging
      const convertedMessages = liveMessages.map((m, i) => {
        if (m.type === "user") {
          return {
            ...m,
            id: `${activeSessionId}-history-${i}`,
            isStreaming: false,
          };
        } else {
          // AI message - preserve existing blocks (from streaming) or build fallback
          let finalBlocks = m.blocks; // Preserve parsed blocks from streaming

          // If no blocks exist, create fallback from content
          if (!finalBlocks || finalBlocks.length === 0) {
            if (m.content) {
              finalBlocks = [{ type: "text", content: m.content }];
            }
          }

          return {
            ...m,
            id: `${activeSessionId}-history-${i}`,
            isStreaming: false,
            blocks: finalBlocks,
          };
        }
      });
      setMessages((prev) => [
        ...prev,
        ...convertedMessages.map((m, i) => ({
          ...m,
          id: `${activeSessionId}-history-${prev.length + i}`,
        })),
      ]);
    }

    // If in a chat and no session, or in share view and no shareId, something is wrong.
    if (!isShareView && !activeSessionId && !isDraftChat) return;
    if (isShareView && !shareId && !adminViewSessionId) return;

    // Build attachments from uploadedFiles before they are cleared
    const messageAttachments = uploadedFiles
      .filter((f) => f.status === "completed")
      .map((f) => ({
        id: f.id,
        path: f.contentUrl,
        filename: f.name,
        icon_type: getIconTypeFromMime(f.type, f.name),
        source: "upload",
        created_at: new Date().toISOString(),
        url: "",
        file_size: f.file?.size || null,
        content_type: f.type || null,
      }));

    // Generate message_id for this conversation turn (shared between human and AI)
    const userMessage = {
      id: Date.now(),
      type: "user",
      content: userMessageContent,
      attachments:
        messageAttachments.length > 0 ? messageAttachments : undefined,
      message_id: null, // Will be updated from server
    };
    const placeholderId = `ai-placeholder-${Date.now()}`; // Create a unique ID upfront

    // Define helper here to avoid re-declaration and scope issues
    const updateLatestAiMessage = (updater) => {
      setLiveMessages((prev) => {
        const next = [...prev];
        const index = next.findLastIndex((m) => m.id === placeholderId);
        if (index !== -1) {
          next[index] = updater(next[index]);
        }
        return next;
      });
    };

    const aiPlaceholder = {
      id: placeholderId,
      type: "ai_loading",
      thinkingSteps: [],
      content: "",
      message_id: null,
      message_type: "chat",
      isStreaming: true,
    };

    const doUpdate = () => {
      const height = chatContentRef.current?.clientHeight || 0;
      flushSync(() => {
        setPlaceholderHeight(height);
        setLiveMessages([userMessage, aiPlaceholder]);
        setDebugRawEvents([]);
        debugRawEventsRef.current = [];
        chatStream.appendBlock({
          type: "user",
          content: userMessageContent,
          attachments:
            messageAttachments.length > 0 ? messageAttachments : undefined,
        });
        chatStream.startStreaming();
        setIsLoading(true);
        setIsStreaming(true);
      });
      liveMessagesStartRef.current?.scrollIntoView({
        block: "start",
        behavior: "auto",
      });
    };

    const canTransition =
      !isMobile && document.startViewTransition && chatContentRef.current;
    if (canTransition) {
      document.startViewTransition(doUpdate);
    } else {
      doUpdate();
    }

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    let currentSessionId = activeSessionId;
    let createdNewSession = false;
    let hasLimitError = false;

    let greetingContext = null;
    if (
      !isAutoSuggest &&
      isDraftChat &&
      greetingMessage &&
      messages.length === 0 &&
      liveMessages.length === 0
    ) {
      greetingContext = greetingMessage.replace(
        "{user}",
        currentUser?.fullName || "bạn",
      );
    }

    try {
      if (isDraftChat && !currentSessionId && !isShareView) {
        const newSession = await apiClient.createSession(userId);
        currentSessionId = newSession.session_id;

        addSession(newSession);
        setActiveSessionId(currentSessionId);
        setIsDraftChat(false);
        localStorage.setItem(Storage.active_session_id, currentSessionId);
        createdNewSession = true;

        syncChatUrl({ sessionId: currentSessionId, isEmbedded, buildPath });
      }

      resetInterruptState();

      wsUpdateAiRef.current = updateLatestAiMessage;
      wsPlaceholderIdRef.current = placeholderId;
      wsUserMessageRef.current = userMessage;
      wsSessionIdRef.current = currentSessionId;

      wsStreamDoneRef.current = () => {
        setIsLoading(false);
        setIsStreaming(false);

        const { blocks } = parseSSEEvents(debugRawEventsRef.current);
        updateLatestAiMessage((msg) => ({
          ...msg,
          isStreaming: false,
          blocks: blocks.length > 0 ? blocks : undefined,
        }));

        setErrorLimitInfo(null);
      };

      const chatPayload = {
        message: userMessageContent,
        sessionId: currentSessionId || undefined,
        shareId: isShareView ? shareId : undefined,
        model: selectedModel || undefined,
        agentId: agentId || undefined,
        contentUrls: contentUrls,
      };
      if (contextDraftRef.current?.source) {
        chatPayload.source = contextDraftRef.current.source;
        chatPayload.source_id = contextDraftRef.current.source_id;
      }
      if (contextsSendRef.current && contextsSendRef.current.length) {
        chatPayload.contexts = contextsSendRef.current;
      }
      agentWs.chat(chatPayload);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Stream aborted by user.");
      } else {
        console.error("Send message error:", error);
        const friendlyMessage =
          error.message === "Failed to fetch"
            ? "Không thể kết nối với máy chủ. Vui lòng kiểm tra kết nối mạng và thử lại."
            : error.message || "Đã xảy ra lỗi. Vui lòng thử lại sau.";
        showError(friendlyMessage);
        updateLatestAiMessage((msg) => ({
          ...msg,
          type: "done",
          isStreaming: false,
        }));
        setIsLoading(false);
        setIsStreaming(false);
      }
    }
  };

  const handleSendMessage = () => {
    const currentValue = inputRef.current?.value?.trim() || "";
    if (!currentValue) return;
    if (isInputDisabled) return;
    // Allow sending during streaming (will use pushMessage)
    if (isLoading && !isStreaming) return;

    let mode = null;
    if (selectedMode === "pro") {
      mode = "pro_search";
    } else if (selectedMode === "deep_search") {
      mode = "deep_search";
    }
    sendMessage(currentValue, mode);

    if (inputRef.current) {
      inputRef.current.clear();
    }

    // Collapse input on mobile after sending
    if (isMobile) {
      setIsInputExpanded(false);
    }
  };

  // Session management
  const startEditing = (session) => {
    setEditingSessionId(session.session_id);
    setEditingName(session.session_name);
  };

  const handleRenameSession = async (sessionId) => {
    if (!editingName.trim()) return;

    try {
      const updatedSession = await apiClient.renameSession(
        sessionId,
        userId,
        editingName.trim(),
      );
      updateSession(sessionId, updatedSession);
    } catch (error) {
      console.error("Error renaming session:", error);
    } finally {
      setEditingSessionId(null);
    }
  };

  const handleDeleteSession = async (sessionIdToDelete) => {
    if (
      !window.confirm(
        "Bạn có chắc chắn muốn xóa cuộc trò chuyện này? Toàn bộ lịch sử sẽ bị mất vĩnh viễn.",
      )
    ) {
      return;
    }

    try {
      await apiClient.deleteSession(sessionIdToDelete, userId);
      removeSession(sessionIdToDelete);
    } catch (error) {
      console.error("Error deleting session:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (mentionOpen && e.key === "Escape") {
      e.preventDefault();
      setMentionOpen(false);
      return;
    }
    if (mentionOpen && (e.key === "Enter" || e.key === "Tab")) {
      e.preventDefault();
      acceptCurrentPageMention();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNotificationClick = useCallback(
    (notification) => {
      setShowNotificationPanel(false);
      isNavigatingToSession.current = true;
      stopAllStreaming(true);
      setIsShareView(false);
      setIsDraftChat(true);
      setActiveSessionId(null);
      setMessages([]);
      chatStream.reset();
      localStorage.removeItem(Storage.active_session_id);
      setLiveMessages([]);
      setDebugRawEvents([]);
      setPlaceholderHeight(0);
      setFollowUpQuestions([]);
      setUploadedFiles([]);
      setDetectedURLs([]);
      setTodosProgress(null);
      setSearchSources(null);
      setWorkspaceFiles(null);
      resetInterruptState();
      wsUpdateAiRef.current = null;
      setContextDraft(null);
      contextDraftRef.current = null;
      fetchContextSentRef.current = null;
      navigate(
        buildPath(`/chat?source=notification&source_id=${notification.id}`),
        { replace: true },
      );
    },
    [navigate],
  );

  const handleLoginSuccess = () => {
    setIsLoginOpen(false);
    // Fetch current user after successful login
    (async () => {
      try {
        const me = await userApi.getCurrent();
        setCurrentUser(me);
        // Reload sessions of this user
        const existingSessions = await apiClient.fetchSessions(getUserId());
        replaceSessions(
          existingSessions && existingSessions.length > 0
            ? existingSessions
            : [],
        );
      } catch (e) {
        console.warn("Không thể tải thông tin người dùng:", e);
      }
    })();
  };

  useEffect(() => {
    registerChatCallbacks({
      handleNewChat,
      switchSession,
      startEditing,
      handleRenameSession,
      handleDeleteSession,
      handleNotificationClick,
      handleLoginSuccess,
    });
  }, [
    handleNewChat,
    switchSession,
    handleNotificationClick,
    registerChatCallbacks,
  ]);

  const handleLogout = useCallback(() => {
    try {
      destroyAgentWS();
      authApi.logout();
      userApi.clearCached();
      clearStorageForLogout();
    } catch {}
    // Reset UI state immediately without reload
    stopStreaming();
    setIsLoading(false);
    setIsInitializing(false);
    setIsDraftChat(true);
    setMessages([]);
    chatStream.reset();
    replaceSessions([]);
    setActiveSessionId(null);
    localStorage.removeItem(Storage.active_session_id);
    setCurrentUser(null);
    setIsUserMenuOpen(false);
    setIsUserSettingsOpen(false);
    setIsSidebarOpen(false);
    setUploadedFiles([]);
    setDetectedURLs([]);
    setTodosProgress(null);
    setSearchSources(null);
    setWorkspaceFiles(null);
    setIsRightPanelOpen(false);
    hasInitialized.current = false;
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    registerChatCallbacks({
      handleLogout,
      openUserSettings: () => setIsUserSettingsOpen(true),
    });
  }, [handleLogout, registerChatCallbacks]);

  // Load current user + membership on first mount if already authenticated
  useEffect(() => {
    if (!isAuthenticated()) return;
    (async () => {
      try {
        const me = await userApi.getCurrent();
        setCurrentUser(me);
        const res = await apiClient.getMembershipInfo();
        const data = res?.data;
        if (data?.membershipStatus) {
          const type = data.membership?.type;
          setMembershipLabel(
            type === "PREMIUM" ? "Professional" : type || "Pro",
          );
        } else {
          setMembershipLabel("Free");
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  // TEMPORARILY DISABLED FOR PERFORMANCE TESTING
  // useEffect(() => {
  //     const timer = setTimeout(() => {
  //         if (inputValue.trim()) {
  //             const urls = extractUniqueURLs(inputValue);
  //             setDetectedURLs(prev => {
  //                 if (JSON.stringify(prev) === JSON.stringify(urls)) {
  //                     return prev;
  //                 }
  //                 return urls;
  //             });
  //         } else {
  //             setDetectedURLs([]);
  //         }
  //     }, 300);

  //     return () => clearTimeout(timer);
  // }, [inputValue]);

  // Auto focus input after initialization is complete
  useEffect(() => {
    if (!isInitializing && inputRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        try {
          inputRef.current?.focus();
        } catch (error) {
          console.log("Could not focus input:", error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isInitializing]);

  const handleSuggestionClick = (suggestion) => {
    if (isLoading || isInitializing) return;
    if (typeof suggestion === "object" && suggestion.content) {
      sendMessage(
        suggestion.content,
        selectedMode === "pro"
          ? "pro_search"
          : selectedMode === "deep_search"
            ? "deep_search"
            : null,
        true,
      );
    } else if (typeof suggestion === "string") {
      sendMessage(
        suggestion,
        selectedMode === "pro"
          ? "pro_search"
          : selectedMode === "deep_search"
            ? "deep_search"
            : null,
        true,
      );
    }
    // Clear input using ref
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setShowSuggestions(false);
  };

  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  });

  useEffect(() => {
    if (!queryParam) return;
    window.history.replaceState({}, "", window.location.pathname);
    sendToChatQueue(queryParam, "url_query");
  }, [queryParam, sendToChatQueue]);

  useEffect(() => {
    const message = chatQueue?.pendingMessage;
    if (!message) return;
    if (isShareView || isAdminView) {
      consumeChatQueue();
      return;
    }
    if (isLoading || isInitializing) return;
    consumeChatQueue();
    sendMessageRef.current?.(message, null, true);
  }, [
    chatQueue?.requestId,
    chatQueue?.pendingMessage,
    isShareView,
    isAdminView,
    isLoading,
    isInitializing,
    consumeChatQueue,
  ]);

  // --- File Upload Handlers ---

  const updateFileInState = (fileId, updates) => {
    setUploadedFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f)),
    );
  };

  const uploadFile = async (fileState) => {
    updateFileInState(fileState.id, { status: "uploading" });

    try {
      // Step 1: Compute SHA256 hash for deduplication
      const contentHash = await computeFileSHA256(fileState.file);

      // Step 2: Get pre-signed POST data from our backend (with hash for deduplication)
      // Using V2 API for file uploads
      const response = await apiClient.getUploadUrlV2(
        fileState.name,
        fileState.type,
        fileState.size,
        contentHash,
      );

      // Check if file is a duplicate
      if (response.is_duplicate && !response.upload_required) {
        console.log(
          "Duplicate file detected, reusing existing:",
          response.content_url,
        );
        updateFileInState(fileState.id, {
          status: "completed",
          contentUrl: response.content_url,
          isDuplicate: true,
        });
        return;
      }

      const { url, fields, content_url } = response;

      if (!url || !fields) {
        throw new Error("Invalid response from server for upload URL.");
      }

      // Step 3: Create a FormData object and append all the fields from the response
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      // IMPORTANT: The file must be the last field appended
      formData.append("file", fileState.file);

      // Step 4: Upload the file directly to S3 using a POST request
      const uploadResponse = await fetch(url, {
        method: "POST",
        body: formData,
        // NO 'Content-Type' header - the browser will set it correctly with the boundary
      });

      if (!uploadResponse.ok) {
        // Try to get error details from S3 response
        const errorText = await uploadResponse.text();
        console.error("S3 Upload Error Response:", errorText);
        throw new Error(
          `S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
        );
      }

      // Step 5: Update file state to completed with content_url
      updateFileInState(fileState.id, {
        status: "completed",
        contentUrl: content_url,
        isDuplicate: false,
      });
    } catch (error) {
      console.error("Upload failed for file:", fileState.name, error);

      // Extract error message from server response
      let errorMessage = "Lỗi upload";

      // Try to parse JSON from error.response (APIError stores raw response text)
      if (error.response) {
        try {
          const errorData = JSON.parse(error.response);
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          // If parse fails, use the raw response or fallback to error.message
          errorMessage = error.response || error.message || "Lỗi upload";
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      updateFileInState(fileState.id, {
        status: "error",
        errorMessage: errorMessage,
      });
    }
  };

  // New handlers for file uploads
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    if (uploadedFiles.length + files.length > MAX_FILES) {
      showError(
        `Bạn chỉ có thể đính kèm tối đa ${MAX_FILES} tệp. Chỉ những tệp đầu tiên sẽ được thêm vào.`,
      );
    }

    const filesToAdd = files.slice(0, MAX_FILES - uploadedFiles.length);

    const newFiles = filesToAdd
      .map((file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          showError(`Loại file ${file.name} không được hỗ trợ.`);
          return null;
        }

        const maxSizeMB = FILE_SIZE_LIMITS[file.type] || MAX_FILE_SIZE_MB;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;

        if (file.size > maxSizeBytes) {
          showError(
            `Tệp ${file.name} quá lớn. Kích thước tối đa cho loại file này là ${maxSizeMB}MB.`,
          );
          return null;
        }
        return {
          id: `${file.name}-${Date.now()}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          previewUrl: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
          status: "pending",
          fileId: null, // To store the ID from the backend
        };
      })
      .filter(Boolean);

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Trigger the upload for each newly added file
    newFiles.forEach(uploadFile);

    event.target.value = null;
  };

  const handleRemoveFile = (fileId) => {
    // Find the file to potentially revoke its URL
    const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
    if (fileToRemove && fileToRemove.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const triggerFileSelect = () => {
    if (uploadedFiles.length >= MAX_FILES) {
      showError(`Bạn đã đạt đến giới hạn tối đa ${MAX_FILES} tệp.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleImagePaste = async (file) => {
    // Check if we can accept more files
    if (uploadedFiles.length >= MAX_FILES) {
      showError(`Bạn đã đạt đến giới hạn tối đa ${MAX_FILES} tệp.`);
      return;
    }

    if (!file.type.startsWith("image/")) {
      showError("Chỉ hỗ trợ paste hình ảnh.");
      return;
    }

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showError("Loại hình ảnh này không được hỗ trợ.");
      return;
    }

    const maxSizeMB = FILE_SIZE_LIMITS[file.type] || 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      showError(`Kích thước file không được vượt quá ${maxSizeMB}MB.`);
      return;
    }

    // Create a custom file object similar to file input
    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileName = `image-${new Date().toISOString().slice(0, 19).replace(/[:-]/g, "")}.${file.type.split("/")[1]}`;

    // Create preview URL for pasted image
    const previewUrl = URL.createObjectURL(file);

    const fileObj = {
      id: fileId,
      file: new File([file], fileName, { type: file.type }),
      name: fileName,
      size: file.size,
      type: file.type,
      previewUrl: previewUrl,
      status: "uploading",
      progress: 0,
      fileId: null, // Will be set after upload
    };

    // Add to uploaded files immediately with feedback
    setUploadedFiles((prev) => [...prev, fileObj]);

    // Show feedback message
    console.log(
      `📷 Đã paste hình ảnh: ${fileName} (${formatFileSize(file.size)})`,
    );

    // Start upload process (similar to handleFileChange)
    try {
      await uploadFile(fileObj);
      console.log(`✅ Upload thành công: ${fileName}`);
    } catch (error) {
      console.error("Error uploading pasted image:", error);

      // Extract error message from server response
      let errorMessage = "Lỗi upload hình ảnh";

      // Try to parse JSON from error.response (APIError stores raw response text)
      if (error.response) {
        try {
          const errorData = JSON.parse(error.response);
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          // If parse fails, use the raw response or fallback to error.message
          errorMessage =
            error.response || error.message || "Lỗi upload hình ảnh";
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Update file status to error with message
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, status: "error", errorMessage: errorMessage }
            : f,
        ),
      );
    }
  };

  return (
    <>
      <div className="flex flex-1 min-w-0">
        <div className="relative flex-1 flex flex-col bg-app-bg min-w-0">
          {/* Conditional Layout based on draft status and messages */}
          {shouldShowShareLoader ? (
            <ShareLoader
              ref={inputSectionRef}
              inputSectionHeight={inputSectionHeight}
              selectedModel={selectedModel}
              onModelChange={handleModelChange}
              isVoiceInputEnabled={isVoiceInputEnabled}
              isMobile={isMobile}
              modelSelectorEnabled={MODEL_SELECTOR_ENABLED}
            />
          ) : showDraftLanding ? (
            !isAuthenticated() ? (
              <div
                className="absolute inset-0 overflow-y-auto"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              >
                <NewHomePage />
              </div>
            ) : (
              /* Greeting centered; input pinned to bottom */
              <>
                {!isMobile && !isEmbedded ? (
                  // Desktop: greeting and input centered as before
                  <div
                    className={`flex-1 flex flex-col items-center ${isEmbedded ? "px-4 sm:px-6" : "px-4 sm:px-8"} ${contextDraft?.active ? "justify-start pt-8" : "justify-center"}`}
                  >
                    {contextDraft?.active ? (
                      <div className="w-full max-w-3xl flex-1 overflow-y-auto pt-8 pb-4">
                        <NotificationDraftView
                          summary={contextDraft.summary}
                          cta={contextDraft.cta}
                          brand={brand}
                          isLoading={isStreaming}
                          onCtaClick={(item) => {
                            const visibleMsg = item.description
                              ? `${item.label}\n${item.description}`
                              : item.label;
                            if (contextDraft?.summary) {
                              const notifMsg = {
                                role: "assistant",
                                type: "chat",
                                content: [
                                  { type: "text", text: contextDraft.summary },
                                ],
                                uuid: `notif-${Date.now()}`,
                              };
                              setMessages([notifMsg]);
                              chatStream.loadHistory([notifMsg]);
                            }
                            sendMessage(visibleMsg);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="text-center mb-6 sm:mb-8">
                        {greetingMessage && (
                          <h1 className="text-xl sm:text-2xl md:text-[28px] font-bold text-gray-900 leading-[1.2] tracking-tight greeting-animate">
                            {greetingMessage.replace(
                              "{user}",
                              currentUser?.fullName || "bạn",
                            )}
                          </h1>
                        )}
                      </div>
                    )}
                    <div
                      className={`w-full max-w-3xl ${isEmbedded ? "px-4 sm:px-6" : "px-4 sm:px-8"}`}
                    >
                      {/* Updated Input Area */}
                      <div
                        className="relative border border-gray-200/80 rounded-[20px] px-5 pt-3 pb-4 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.1)]"
                        style={{
                          overflow: "visible",
                          transition: "border-color 0.2s, box-shadow 0.3s",
                        }}
                      >
                        {contextFeatureEnabled && (
                          <MentionPopover
                            open={mentionOpen}
                            currentPage={currentPageContext}
                            onPick={() => acceptCurrentPageMention()}
                            onClose={() => setMentionOpen(false)}
                          />
                        )}
                        {uploadedFiles.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {uploadedFiles.map((file) => (
                              <UploadedFileItem
                                key={file.id}
                                file={file}
                                onRemove={handleRemoveFile}
                              />
                            ))}
                          </div>
                        )}
                        {contextFeatureEnabled && <ChatContextChipRow />}

                        {isVoiceInputInitializing ||
                        isVoiceInputRecording ||
                        isVoiceInputTranscribing ||
                        voiceInputNoSpeechError ? (
                          <VoiceInputRecording
                            audioLevel={voiceInputAudioLevel}
                            duration={voiceInputDuration}
                            isInitializing={isVoiceInputInitializing}
                            isTranscribing={isVoiceInputTranscribing}
                            partialTranscript={voiceInputPartialTranscript}
                            noSpeechError={voiceInputNoSpeechError}
                            onCancel={
                              voiceInputNoSpeechError
                                ? dismissVoiceInputError
                                : cancelVoiceInput
                            }
                            onSend={stopVoiceInput}
                            onRetry={retryVoiceInput}
                          />
                        ) : (
                          <>
                            <div
                              className="relative"
                              style={{ maxHeight: "300px", overflowY: "auto" }}
                            >
                              <InputWithInlineURLs
                                ref={inputRef}
                                className="w-full resize-none text-base focus:outline-none"
                                onKeyDown={handleKeyDown}
                                onInput={() => {
                                  setHasInputText(!!inputRef.current?.value);
                                  if (contextFeatureEnabled)
                                    detectMentionTrigger();
                                }}
                                onImagePaste={handleImagePaste}
                                onClick={() => {
                                  if (showDraftLanding)
                                    setShowSuggestions(true);
                                }}
                                onBlur={() => setShowSuggestions(false)}
                                placeholder={
                                  showDraftLanding ? "" : "Hỏi bất kỳ điều gì"
                                }
                                rows={1}
                                disabled={isInputDisabled}
                                style={{
                                  minHeight: "40px",
                                  paddingTop: "4px",
                                  overflow: "hidden",
                                  whiteSpace: "pre-wrap",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              />
                              {showDraftLanding && !hasInputText && (
                                <span
                                  className="absolute left-0 text-base text-gray-400 pointer-events-none typing-placeholder"
                                  style={{ top: "4px", lineHeight: "24px" }}
                                >
                                  Hỏi bất kỳ điều gì
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  ref={fileInputRef}
                                  onChange={handleFileSelect}
                                  className="hidden"
                                  multiple
                                  accept={ALLOWED_FILE_TYPES.join(",")}
                                />
                                <button
                                  onClick={triggerFileSelect}
                                  disabled={uploadedFiles.length >= MAX_FILES}
                                  className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                                >
                                  <Plus
                                    className="w-3.5 h-3.5"
                                    strokeWidth={2}
                                  />
                                </button>
                                <DevToolsMenu
                                  selectedModel={selectedModel}
                                  onModelChange={handleModelChange}
                                  disabled={isStreaming || isInputDisabled}
                                  agentId={agentId}
                                  agentName={agentName}
                                  onAgentChange={setAgent}
                                  onClearAgent={clearAgentId}
                                  onSkillInsert={handleSkillInsert}
                                  onAgentNameHydrate={hydrateAgentName}
                                />
                                {MODEL_SELECTOR_ENABLED &&
                                  !isDevModelEnabled() && (
                                    <ModelSelector
                                      selectedModel={selectedModel}
                                      onModelChange={handleModelChange}
                                      disabled={isStreaming || isInputDisabled}
                                    />
                                  )}
                              </div>
                              <div className="flex items-center gap-2">
                                {DEBUG_ENABLED && !isEmbedded && (
                                  <VoiceButton
                                    isLoading={isVoiceConnecting}
                                    isRecording={isVoiceRecording}
                                    isSpeaking={isVoiceSpeaking}
                                    disabled={isStreaming || !isAuthenticated()}
                                    onStartRecording={startVoiceRecording}
                                    onStopRecording={stopVoiceRecording}
                                    onInterrupt={interruptVoice}
                                  />
                                )}
                                {DEBUG_ENABLED && !isEmbedded && (
                                  <VoiceUploadButton
                                    onUpload={uploadVoiceAudioFile}
                                    disabled={isStreaming || !isAuthenticated()}
                                    isUploading={isVoiceRecording}
                                  />
                                )}
                                {isVoiceInputEnabled && (
                                  <VoiceInputButton
                                    onClick={startVoiceInput}
                                    disabled={
                                      isStreaming ||
                                      isInputDisabled ||
                                      !isAuthenticated()
                                    }
                                    hideTooltip={isMobile}
                                  />
                                )}
                                <div className="relative w-7 h-7 flex-shrink-0">
                                  <ComposerSendButton
                                    isStopping={isStopping}
                                    isStreaming={isStreaming}
                                    isDisabled={isInputDisabled}
                                    onSend={handleSendMessage}
                                    onTerminate={handleTerminate}
                                    buttonDisabled={
                                      isStopping ||
                                      isLoading ||
                                      (!isStreaming && isInputDisabled)
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {/* {suggestionsList} */}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Mobile / embedded: greeting pinned near bottom, input absolute at bottom
                  <>
                    {/* Greeting Area - Absolute with full height */}
                    <div
                      className="absolute inset-0 overflow-y-auto px-4 messages-padding-top"
                      style={{
                        paddingBottom: `${inputSectionHeight + 16}px`,
                      }}
                    >
                      <div style={{ height: `${MESSAGES_BASE_SPACING}px` }} />
                      {isEmbedded ? (
                        <div className="flex flex-col justify-end min-h-full pb-2">
                          <div className="w-full max-w-3xl mx-auto">
                            <h1 className="text-[15px] font-semibold text-gray-900 leading-tight mb-3 greeting-animate-fast">
                              {greetingMessage
                                ? greetingMessage.replace(
                                    "{user}",
                                    currentUser?.fullName || "bạn",
                                  )
                                : "Tôi có thể giúp gì cho bạn?"}
                            </h1>
                            {pageSuggestionsReady && (
                              <ul className="flex flex-wrap gap-2">
                                {embeddedDraftSuggestions.map((s, idx) => {
                                  const Icon = s.icon || Lightbulb;
                                  return (
                                    <li
                                      key={s.label}
                                      className="suggestion-animate"
                                      style={{
                                        animationDelay: `${0.18 + idx * 0.06}s`,
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (isInputDisabled) return;
                                          handleSuggestionClick(s.prompt);
                                        }}
                                        disabled={isInputDisabled}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-[13px] text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Icon className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" />
                                        <span className="truncate max-w-[260px]">
                                          {s.label}
                                        </span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center min-h-full">
                          <div className="text-center">
                            {greetingMessage && (
                              <h1 className="text-2xl sm:text-2xl font-bold text-gray-900 leading-[1.2] tracking-tight greeting-animate">
                                {greetingMessage.replace(
                                  "{user}",
                                  currentUser?.fullName || "bạn",
                                )}
                              </h1>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      className="fixed sm:absolute bottom-0 left-0 right-0 pt-0 pb-4 px-4 z-10"
                      style={{
                        paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
                      }}
                    >
                      <BottomFadeMask height={120} fadeStart="3rem" />

                      <div className="max-w-3xl w-full mx-auto relative z-[1]">
                        {/* Updated Input Area */}
                        <div
                          className="relative border border-gray-200/80 rounded-[20px] px-4 py-3 bg-white shadow-[0_2px_16px_rgba(0,0,0,0.06)] flex flex-col"
                          style={{
                            overflow: "visible",
                            transition: "border-color 0.2s, box-shadow 0.3s",
                          }}
                        >
                          {contextFeatureEnabled && (
                            <MentionPopover
                              open={mentionOpen}
                              currentPage={currentPageContext}
                              onPick={() => acceptCurrentPageMention()}
                              onClose={() => setMentionOpen(false)}
                            />
                          )}

                          {uploadedFiles.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2 order-1">
                              {uploadedFiles.map((file) => (
                                <UploadedFileItem
                                  key={file.id}
                                  file={file}
                                  onRemove={handleRemoveFile}
                                />
                              ))}
                            </div>
                          )}
                          {contextFeatureEnabled && (
                            <div className="order-1">
                              <ChatContextChipRow />
                            </div>
                          )}

                          {isVoiceInputInitializing ||
                          isVoiceInputRecording ||
                          isVoiceInputTranscribing ||
                          voiceInputNoSpeechError ? (
                            <VoiceInputRecording
                              audioLevel={voiceInputAudioLevel}
                              duration={voiceInputDuration}
                              isInitializing={isVoiceInputInitializing}
                              isTranscribing={isVoiceInputTranscribing}
                              partialTranscript={voiceInputPartialTranscript}
                              noSpeechError={voiceInputNoSpeechError}
                              onCancel={
                                voiceInputNoSpeechError
                                  ? dismissVoiceInputError
                                  : cancelVoiceInput
                              }
                              onSend={stopVoiceInput}
                              onRetry={retryVoiceInput}
                            />
                          ) : (
                            <>
                              <div
                                className="order-2 relative"
                                style={{
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                }}
                              >
                                <InputWithInlineURLs
                                  ref={inputRef}
                                  className="w-full resize-none text-base focus:outline-none"
                                  onKeyDown={handleKeyDown}
                                  onInput={() => {
                                    setHasInputText(!!inputRef.current?.value);
                                    if (contextFeatureEnabled)
                                      detectMentionTrigger();
                                  }}
                                  onImagePaste={handleImagePaste}
                                  onClick={() => {
                                    if (showDraftLanding)
                                      setShowSuggestions(true);
                                  }}
                                  onBlur={() => setShowSuggestions(false)}
                                  placeholder={
                                    showDraftLanding ? "" : "Hỏi bất kỳ điều gì"
                                  }
                                  rows={1}
                                  disabled={isInputDisabled}
                                  style={{
                                    minHeight: "24px",
                                    overflow: "hidden",
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                />
                                {showDraftLanding && !hasInputText && (
                                  <span className="absolute left-0 top-0 text-base text-gray-400 pointer-events-none typing-placeholder leading-[24px]">
                                    Hỏi bất kỳ điều gì
                                  </span>
                                )}
                              </div>
                              {/* {suggestionsList} */}
                              <div className="flex items-center justify-between mt-2 order-3">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    multiple
                                    accept={ALLOWED_FILE_TYPES.join(",")}
                                  />
                                  <button
                                    onClick={triggerFileSelect}
                                    disabled={uploadedFiles.length >= MAX_FILES}
                                    className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                                  >
                                    <Plus
                                      className="w-3.5 h-3.5"
                                      strokeWidth={2}
                                    />
                                  </button>
                                  <DevToolsMenu
                                    selectedModel={selectedModel}
                                    onModelChange={handleModelChange}
                                    disabled={isStreaming || isInputDisabled}
                                    agentId={agentId}
                                    agentName={agentName}
                                    onAgentChange={setAgent}
                                    onClearAgent={clearAgentId}
                                    onSkillInsert={handleSkillInsert}
                                    onAgentNameHydrate={hydrateAgentName}
                                  />
                                  {MODEL_SELECTOR_ENABLED &&
                                    !isDevModelEnabled() && (
                                      <ModelSelector
                                        selectedModel={selectedModel}
                                        onModelChange={handleModelChange}
                                        disabled={
                                          isStreaming || isInputDisabled
                                        }
                                      />
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {DEBUG_ENABLED && !isEmbedded && (
                                    <VoiceButton
                                      isLoading={isVoiceConnecting}
                                      isRecording={isVoiceRecording}
                                      isSpeaking={isVoiceSpeaking}
                                      disabled={
                                        isStreaming || !isAuthenticated()
                                      }
                                      onStartRecording={startVoiceRecording}
                                      onStopRecording={stopVoiceRecording}
                                      onInterrupt={interruptVoice}
                                    />
                                  )}
                                  {DEBUG_ENABLED && !isEmbedded && (
                                    <VoiceUploadButton
                                      onUpload={uploadVoiceAudioFile}
                                      disabled={
                                        isStreaming || !isAuthenticated()
                                      }
                                      isUploading={isVoiceRecording}
                                    />
                                  )}
                                  {isVoiceInputEnabled && (
                                    <VoiceInputButton
                                      onClick={startVoiceInput}
                                      disabled={
                                        isStreaming ||
                                        isInputDisabled ||
                                        !isAuthenticated()
                                      }
                                      hideTooltip={isMobile}
                                    />
                                  )}
                                  <div className="relative w-7 h-7 flex-shrink-0">
                                    <ComposerSendButton
                                      isStopping={isStopping}
                                      isStreaming={isStreaming}
                                      isDisabled={isInputDisabled}
                                      onSend={handleSendMessage}
                                      onTerminate={handleTerminate}
                                      buttonDisabled={
                                        isStopping ||
                                        isLoading ||
                                        (!isStreaming && isInputDisabled)
                                      }
                                      iconSize="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                      streamingBlock="w-2.5 h-2.5 sm:w-3 sm:h-3"
                                    />
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )
          ) : (
            /* Normal Layout for Messages */
            <>
              {/* Messages Area - Full height with padding for fixed input */}
              <div
                className={`absolute inset-0 overflow-y-auto auto-hide-scrollbar messages-padding-top chat-scroll-root ${isEmbedded ? "px-4 sm:px-6" : "px-4 sm:px-8"}`}
                ref={chatContentRef}
                style={{
                  paddingBottom: `${inputSectionHeight + (isShareView && !isAuthenticated() ? MASK_FADE_HEIGHT_SHARE_UNAUTH : isMobile && !isInputExpanded ? MASK_FADE_HEIGHT_MOBILE : MASK_FADE_HEIGHT) + MASK_BUFFER}px`,
                }}
              >
                {/*<div*/}
                {/*    className="sticky left-0 right-0 pointer-events-none z-20"*/}
                {/*    style={{*/}
                {/*        top: '0',*/}
                {/*        height: '20px',*/}
                {/*        background: 'linear-gradient(to bottom, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 30%, rgba(255,255,255,0.3) 60%, transparent 100%)',*/}
                {/*        marginLeft: '-1rem',*/}
                {/*        marginRight: '-1rem'*/}
                {/*    }}*/}
                {/*/>*/}
                {(!isMessagesVisible || isSwitchingSession) && !!sessionId && (
                  <div className="relative max-w-3xl w-full mx-auto py-8 overflow-hidden">
                    {/* User bubble — right */}
                    <div className="flex justify-end mb-8">
                      <div className="h-10 bg-app-hover animate-pulse rounded-2xl w-56" />
                    </div>
                    {/* AI lines — left */}
                    <div className="space-y-3">
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-full" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[82%]" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[95%]" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[70%]" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[88%]" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[93%]" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[62%]" />
                      <div className="h-4 bg-app-hover animate-pulse rounded-full w-[78%]" />
                    </div>
                    {/* Gradient fade bottom → top */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(to top, var(--app-bg) 10%, transparent 100%)",
                      }}
                    />
                  </div>
                )}
                <div
                  className="max-w-3xl w-full mx-auto"
                  style={{
                    opacity: isMessagesVisible && !isSwitchingSession ? 1 : 0,
                    transition: "none",
                  }}
                >
                  <div style={{ height: `${MESSAGES_BASE_SPACING}px` }} />

                  {/* History Messages OR Debug History Blocks */}
                  {DEBUG_ENABLED &&
                  debugRawHistory?.data &&
                  Array.isArray(debugRawHistory.data) ? (
                    // DEBUG MODE: Render raw JSON from history
                    debugRawHistory.data.map((msg, index) => {
                      const typeInfo = msg?.type ? `type: ${msg.type}` : "";
                      const roleInfo = msg?.role ? `role: ${msg.role}` : "";
                      const headerInfo = [typeInfo, roleInfo]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <div
                          key={`debug-history-${index}`}
                          className="mb-3 p-3 rounded-lg bg-gray-50"
                        >
                          <div className="text-xs text-gray-500 mb-1">
                            [{index}] {headerInfo}
                          </div>
                          <pre className="text-xs font-mono bg-white p-2 rounded overflow-x-auto max-h-[400px] overflow-y-auto text-gray-700 whitespace-pre-wrap">
                            {JSON.stringify(msg, null, 2)}
                          </pre>
                        </div>
                      );
                    })
                  ) : (
                    <ChatStreamView
                      blocks={chatStream.blocks}
                      isStreaming={chatStream.isStreaming}
                      brand={brand}
                      placeholderHeight={placeholderHeight}
                      liveRef={liveMessagesStartRef}
                      interruptSessionId={interruptSessionId}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onRetry={() => sendMessage("Tiếp tục")}
                      isSubmitting={isSubmittingApproval}
                      sessionId={activeSessionId || sharedInfo?.session_id}
                      cta={contextDraft?.cta}
                      onCtaClick={(item) => {
                        const visibleMsg = item.description
                          ? `${item.label}\n${item.description}`
                          : item.label;
                        sendMessage(visibleMsg);
                      }}
                    />
                  )}

                  {/* Voice Partial Transcript - Show as user message during voice input */}
                  {/* Show while partial exists, pulse only when recording */}
                  {voicePartialTranscript && (
                    <div className={isVoiceRecording ? "animate-pulse" : ""}>
                      <UserMessageBlock content={voicePartialTranscript} />
                    </div>
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>

              <ScrollDownButton
                ref={scrollDownButtonRef}
                bottom={inputSectionHeight + 16}
                left={
                  isAuthenticated() && !isMobile
                    ? isSidebarOpen
                      ? "256px"
                      : "52px"
                    : "0"
                }
                isEmbedded={isEmbedded}
                onClick={scrollToBottom}
              />

              {/* Follow-up questions - REMOVED FROM HERE */}

              {/* Bottom Input Area - Absolute at bottom */}
              <div
                className={`fixed sm:absolute bottom-0 left-0 right-0 pt-0 pb-2 z-[1] ${isEmbedded ? "px-4 sm:px-6" : "px-4 sm:px-8"}`}
                style={{
                  paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))",
                  paddingRight: isMobile
                    ? "calc(1rem + 6px)"
                    : "calc(2rem + 6px)",
                }}
                ref={inputSectionRef}
              >
                {/* Bottom fade mask overlay */}
                <div
                  className="absolute bottom-0 left-4 right-4 pointer-events-none transition-all duration-100"
                  style={{
                    height:
                      isShareView && !isAuthenticated()
                        ? "240px"
                        : isMobile && !isInputExpanded
                          ? "140px"
                          : "180px",
                    WebkitMaskImage:
                      isShareView && !isAuthenticated()
                        ? "linear-gradient(transparent, #000 7rem)"
                        : isMobile && !isInputExpanded
                          ? "linear-gradient(transparent, #000 3.5rem)"
                          : "linear-gradient(transparent, #000 5rem)",
                    maskImage:
                      isShareView && !isAuthenticated()
                        ? "linear-gradient(transparent, #000 7rem)"
                        : isMobile && !isInputExpanded
                          ? "linear-gradient(transparent, #000 3.5rem)"
                          : "linear-gradient(transparent, #000 5rem)",
                  }}
                >
                  <div className="absolute -left-4 -right-4 bottom-0 top-0 overflow-hidden">
                    <div className="absolute w-full h-full bg-app-bg"></div>
                  </div>
                </div>

                <div className="max-w-3xl w-full mx-auto relative z-[1]">
                  {isShareView && !isAuthenticated() && (
                    <p className="text-sm text-center text-gray-500 mb-2">
                      Để tiếp tục cuộc trò chuyện này, hãy đăng nhập và gửi tin
                      nhắn.
                    </p>
                  )}
                  {/* Usage limit warning banner */}
                  <TodoProgressCompact todos={todosProgress} />
                  {/* Question UI - overlays input area when ask_user_question */}
                  {!!interruptSessionId &&
                  interruptData?.actionRequests?.some(
                    (a) => a.name === "ask_user_question",
                  ) ? (
                    (() => {
                      const questionAction = interruptData.actionRequests.find(
                        (a) => a.name === "ask_user_question",
                      );
                      const questions = extractQuestions(questionAction);
                      if (questions.length === 0) return null;
                      return (
                        <>
                          <div className="relative border border-app-border rounded-2xl bg-white shadow-sm p-4 sm:p-5">
                            <QuestionUI
                              questions={questions}
                              onSubmit={(data) => {
                                handleApprove(data);
                              }}
                              isSubmitting={isSubmittingApproval}
                            />
                          </div>
                          <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                            <span>&#8593;&#8595; to navigate</span>
                            <span>·</span>
                            <span>Enter to select</span>
                            <span>·</span>
                            <span>Esc to skip</span>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <>
                      {/* Updated Input Area */}
                      <div
                        className="relative border border-gray-200/80 rounded-[24px] bg-white shadow-[0_2px_16px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow] duration-100 ease-in-out"
                        style={{
                          overflow: "visible",
                          padding:
                            isMobile && !isInputExpanded
                              ? "10px 12px"
                              : "12px 16px",
                          paddingBottom:
                            isMobile && !isInputExpanded ? "10px" : "12px",
                          minHeight: isMobile ? "48px" : "auto",
                        }}
                      >
                        {contextFeatureEnabled &&
                          (!isMobile || isInputExpanded) && (
                            <MentionPopover
                              open={mentionOpen}
                              currentPage={currentPageContext}
                              onPick={() => acceptCurrentPageMention()}
                              onClose={() => setMentionOpen(false)}
                            />
                          )}
                        {/* Hidden file input - always rendered */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          className="hidden"
                          multiple
                          accept={ALLOWED_FILE_TYPES.join(",")}
                        />

                        {/* Mobile View - Fixed button positions */}
                        {isMobile && !isInputExpanded && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                triggerFileSelect();
                              }}
                              disabled={uploadedFiles.length >= MAX_FILES}
                              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                            >
                              <Plus className="w-4 h-4" strokeWidth={2.5} />
                            </button>

                            {/* Input area - grows to fill space */}
                            <div
                              className={`flex-1 text-base placeholder-gray-500 bg-transparent ${isInputDisabled ? "cursor-not-allowed opacity-50" : "cursor-text"}`}
                              onClick={() => {
                                if (!isInputDisabled) setIsInputExpanded(true);
                              }}
                            >
                              <span className="text-gray-500">
                                Hỏi bất kỳ điều gì
                              </span>
                            </div>

                            {/* Send button - fixed position right */}
                            <div className="relative w-7 h-7 flex-shrink-0">
                              {/* {isStreaming && (
                                                    <div className="pointer-events-none absolute inset-0 -m-1 rounded-full border-2 border-gray-800 border-t-transparent animate-spin" />
                                                )} */}
                              <ComposerSendButton
                                isStopping={isStopping}
                                isStreaming={isStreaming}
                                isDisabled={isInputDisabled || isShareLoading}
                                onSend={handleSendMessage}
                                onTerminate={handleTerminate}
                                buttonDisabled={
                                  isStopping ||
                                  (!isStreaming &&
                                    (isInputDisabled || isShareLoading))
                                }
                                iconSize="w-3.5 h-3.5"
                                streamingBlock="w-2.5 h-2.5"
                              />
                            </div>
                          </div>
                        )}

                        {/* Desktop or Mobile Expanded View */}
                        {(!isMobile || isInputExpanded) && (
                          <>
                            {/* Uploaded files display */}
                            {uploadedFiles.length > 0 && (
                              <div className="mb-3 flex flex-wrap gap-2">
                                {uploadedFiles.map((file) => (
                                  <UploadedFileItem
                                    key={file.id}
                                    file={file}
                                    onRemove={handleRemoveFile}
                                  />
                                ))}
                              </div>
                            )}
                            {contextFeatureEnabled && <ChatContextChipRow />}

                            {isVoiceInputInitializing ||
                            isVoiceInputRecording ||
                            isVoiceInputTranscribing ||
                            voiceInputNoSpeechError ? (
                              <VoiceInputRecording
                                audioLevel={voiceInputAudioLevel}
                                duration={voiceInputDuration}
                                isInitializing={isVoiceInputInitializing}
                                isTranscribing={isVoiceInputTranscribing}
                                partialTranscript={voiceInputPartialTranscript}
                                noSpeechError={voiceInputNoSpeechError}
                                onCancel={
                                  voiceInputNoSpeechError
                                    ? dismissVoiceInputError
                                    : cancelVoiceInput
                                }
                                onSend={stopVoiceInput}
                                onRetry={retryVoiceInput}
                              />
                            ) : (
                              <>
                                <div
                                  className="relative"
                                  style={{
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <InputWithInlineURLs
                                    ref={inputRef}
                                    className="w-full resize-none text-base focus:outline-none placeholder-gray-500"
                                    onKeyDown={handleKeyDown}
                                    onInput={() => {
                                      if (contextFeatureEnabled)
                                        detectMentionTrigger();
                                    }}
                                    onImagePaste={handleImagePaste}
                                    onClick={() => {
                                      if (showDraftLanding)
                                        setShowSuggestions(true);
                                    }}
                                    onBlur={() => {
                                      setTimeout(
                                        () => setShowSuggestions(false),
                                        200,
                                      );
                                      // Collapse on blur if empty and on mobile
                                      if (
                                        isMobile &&
                                        !getInputValue() &&
                                        !uploadedFiles.length
                                      ) {
                                        setTimeout(
                                          () => setIsInputExpanded(false),
                                          100,
                                        );
                                      }
                                    }}
                                    placeholder="Hỏi bất kỳ điều gì"
                                    rows={1}
                                    disabled={isInputDisabled}
                                    style={{
                                      minHeight: "24px",
                                      overflow: "hidden",
                                      whiteSpace: "pre-wrap",
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                    }}
                                  />
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <div
                                    className="flex items-center gap-2"
                                    onMouseDown={(e) => {
                                      // Prevent textarea blur when clicking buttons on mobile
                                      if (isMobile) {
                                        e.preventDefault();
                                      }
                                    }}
                                  >
                                    <button
                                      onClick={triggerFileSelect}
                                      disabled={
                                        uploadedFiles.length >= MAX_FILES
                                      }
                                      className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
                                    >
                                      <Plus
                                        className="w-3.5 h-3.5"
                                        strokeWidth={2}
                                      />
                                    </button>
                                    <DevToolsMenu
                                      selectedModel={selectedModel}
                                      onModelChange={handleModelChange}
                                      disabled={
                                        isStreaming ||
                                        isInputDisabled ||
                                        isShareLoading
                                      }
                                      agentId={agentId}
                                      agentName={agentName}
                                      onAgentChange={setAgent}
                                      onClearAgent={clearAgentId}
                                      onSkillInsert={handleSkillInsert}
                                      onAgentNameHydrate={hydrateAgentName}
                                    />
                                    {MODEL_SELECTOR_ENABLED &&
                                      !isDevModelEnabled() && (
                                        <ModelSelector
                                          selectedModel={selectedModel}
                                          onModelChange={handleModelChange}
                                          disabled={
                                            isStreaming ||
                                            isInputDisabled ||
                                            isShareLoading
                                          }
                                        />
                                      )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {DEBUG_ENABLED && !isEmbedded && (
                                      <VoiceButton
                                        isLoading={isVoiceConnecting}
                                        isRecording={isVoiceRecording}
                                        isSpeaking={isVoiceSpeaking}
                                        disabled={
                                          isStreaming || !isAuthenticated()
                                        }
                                        onStartRecording={startVoiceRecording}
                                        onStopRecording={stopVoiceRecording}
                                        onInterrupt={interruptVoice}
                                      />
                                    )}
                                    {DEBUG_ENABLED && !isEmbedded && (
                                      <VoiceUploadButton
                                        onUpload={uploadVoiceAudioFile}
                                        disabled={
                                          isStreaming || !isAuthenticated()
                                        }
                                        isUploading={isVoiceRecording}
                                      />
                                    )}
                                    {isVoiceInputEnabled && (
                                      <VoiceInputButton
                                        onClick={startVoiceInput}
                                        disabled={
                                          isStreaming ||
                                          isInputDisabled ||
                                          isShareLoading ||
                                          !isAuthenticated()
                                        }
                                        hideTooltip={isMobile}
                                      />
                                    )}
                                    <div className="relative w-7 h-7 flex-shrink-0">
                                      <ComposerSendButton
                                        isStopping={isStopping}
                                        isStreaming={isStreaming}
                                        isDisabled={
                                          isInitializing || isShareLoading
                                        }
                                        onSend={handleSendMessage}
                                        onTerminate={handleTerminate}
                                        buttonDisabled={
                                          isStopping ||
                                          (!isStreaming &&
                                            (isInputDisabled || isShareLoading))
                                        }
                                        iconSize="w-3.5 h-3.5 sm:w-4 sm:h-4"
                                        streamingBlock="w-2.5 h-2.5 sm:w-3 sm:h-3"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      {/* Disclaimer Text - Outside input border */}
                      <div className="mt-2 text-center">
                        <p className="text-[9px] sm:text-[10px] text-gray-500">
                          Simplize không chịu trách nhiệm cho mọi rủi ro hoặc
                          thiệt hại phát sinh từ việc sử dụng thông tin Nebula
                          cung cấp để đưa ra quyết định đầu tư.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <RightPanel
          isOpen={isRightPanelOpen}
          isEmbedded={isEmbedded}
          todosProgress={todosProgress}
          searchSources={searchSources}
          workspaceFiles={workspaceFiles}
          sessionId={activeSessionId || sharedInfo?.session_id}
        />
      </div>

      <ChatModals
        viewingAttachment={viewingAttachment}
        setViewingAttachment={setViewingAttachment}
        activeSessionId={activeSessionId}
        sharedInfo={sharedInfo}
        isUserMenuOpen={isUserMenuOpen}
        setIsUserMenuOpen={setIsUserMenuOpen}
        currentUser={currentUser}
        onOpenSettings={() => setIsUserSettingsOpen(true)}
        onLogout={handleLogout}
        isUserSettingsOpen={isUserSettingsOpen}
        setIsUserSettingsOpen={setIsUserSettingsOpen}
        settingsDefaultTab={settingsDefaultTab}
        setSettingsDefaultTab={setSettingsDefaultTab}
        injectVoiceInputFile={injectVoiceInputFile}
        isVoiceInputRecording={isVoiceInputRecording}
        showPaywall={showPaywall}
        setShowPaywall={setShowPaywall}
        errorLimitInfo={errorLimitInfo}
        budgetStatus={budgetStatus}
        showPromotionPopup={showPromotionPopup}
        setShowPromotionPopup={setShowPromotionPopup}
        checkUsageStatus={checkUsageStatus}
        showUnavailable={showUnavailable}
        setShowUnavailable={setShowUnavailable}
        showLimitReached={showLimitReached}
        setShowLimitReached={setShowLimitReached}
      />
    </>
  );
};

export default ChatApp;
